import React, { useState, useEffect, useRef, Fragment } from 'react';
import { getFirestore, doc, getDoc, collection, onSnapshot, updateDoc, arrayUnion, addDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { InvoiceService } from '../services/InvoiceService';
import { toast } from 'react-hot-toast';
import { useStripe } from '../contexts/StripeContext';
import { useEmail } from '../contexts/EmailContext';
import CustomerList from './CustomerList';
import ServiceList from './ServiceList';
import LineItemsManager from './LineItemsManager';
import TaxCalculator from './TaxCalculator';
import EmailService from '../services/EmailService';
import EmailTemplateService from '../services/EmailTemplateService';
import EmailTemplateManager from './EmailTemplateManager';
import EmailAccountsModal from './EmailAccountsModal';
import leadIntakeService from '../services/LeadIntakeService';
import { motion, AnimatePresence } from 'framer-motion';
import { useAnalytics } from '../hooks/useAnalytics';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

// Add scrollbar hide styles
const scrollbarHideStyles = `
  .scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
  .scrollbar-hide::-webkit-scrollbar {
    display: none;
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = scrollbarHideStyles;
  document.head.appendChild(style);
}

const InvoiceModal = ({ isOpen, onClose, project, client, email, user }) => {
  const navigate = useNavigate();
  const { openCompose } = useEmail() || {};
  const { isOnboarded, stripeAccountId } = useStripe();
  const { trackInvoiceCreated, trackInvoiceSent, trackFeatureUsed } = useAnalytics();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [stripeCapabilityError, setStripeCapabilityError] = useState(false);
  const [showEmailAccountsModal, setShowEmailAccountsModal] = useState(false);
  const [emailAccountError, setEmailAccountError] = useState(false);
  const [services, setServices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [newClientEmail, setNewClientEmail] = useState('');
  const [showNewClientForm, setShowNewClientForm] = useState(false);
  const [userAccounts, setUserAccounts] = useState([]);
  
  // Add state for dropdown visibility
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [showServiceDropdown, setShowServiceDropdown] = useState(false);
  const [activeRecipientDropdown, setActiveRecipientDropdown] = useState(null); // Track which recipient field has active dropdown
  
  // Tab navigation state
  const [activeTab, setActiveTab] = useState('basics');
  
  // Payment email settings state
  const [paymentEmailSettings, setPaymentEmailSettings] = useState({
    sendConfirmationEmail: false,
    emailMode: 'professional', // 'professional', 'friendly', 'custom'
    customMessage: '',
    notifyCreator: true,
    includeReceipt: true
  });
  
  // Survey settings state
  const [surveySettings, setSurveySettings] = useState({
    createSurvey: false,
    surveyTitle: '',
    anonymous: false,
    questions: []
  });
  
  // Email template state
  const [customTemplates, setCustomTemplates] = useState([]);
  const [showTemplateManager, setShowTemplateManager] = useState(false);
  const [templatesLoading, setTemplatesLoading] = useState(false);

  // Signature settings state
  const [signatureSettings, setSignatureSettings] = useState(null);
  
  // Add refs for the dropdown containers
  const clientDropdownRef = useRef(null);
  const serviceDropdownRef = useRef(null);
  const newClientDebounceRef = useRef(null);
  const recipientDropdownRefs = useRef({}); // Store refs for each recipient dropdown
  
  // Client editing state
  const [isEditingClient, setIsEditingClient] = useState(false);
  const [clientSaved, setClientSaved] = useState(false);
  const [clientCompleted, setClientCompleted] = useState(false); // Track when customer selection is complete

  // Initialize invoice data with project and client details if available
  console.log('Initial Email Data:', {
    rawFrom: email?.from,
    sender: email?.sender,
    emailMatch: email?.sender ? email.sender.match(/<([^>]+)>/) : null
  });

  const getEmailFromString = (str) => {
    if (!str) return '';
    // Try to extract from angle brackets first
    const angleEmailMatch = str.match(/<([^>]+)>/);
    if (angleEmailMatch) {
      return angleEmailMatch[1];
    }
    // If no angle brackets, try to find a plain email
    const plainEmailMatch = str.match(/([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9._-]+)/);
    if (plainEmailMatch) {
      return plainEmailMatch[1];
    }
    return str;
  };

  // Initialize with business information from profile
  useEffect(() => {
    const fetchBusinessInfo = async () => {
      if (!user) return;
      
      try {
        const firestore = getFirestore();
        const profileRef = doc(firestore, 'profiles', user.uid);
        const profileDoc = await getDoc(profileRef);
        
        if (profileDoc.exists()) {
          const profileData = profileDoc.data();
          const businessName = profileData.name || profileData.businessName || '';
          
          // Update invoice data with business information if not already set
          setInvoiceData(prev => ({
            ...prev,
            fromName: prev.fromName || businessName,
            businessInfo: {
              businessName: businessName,
              professionalTitle: profileData.professionalTitle || '',
              email: profileData.contactInfo?.email || user.email || '',
              phone: profileData.contactInfo?.phone || '',
              location: profileData.contactInfo?.location || ''
            }
          }));
        }
      } catch (error) {
        console.error('Error fetching business info:', error);
      }
    };

    fetchBusinessInfo();
  }, [user]);

  // Load signature settings
  useEffect(() => {
    const fetchSignatureSettings = async () => {
      if (!user?.uid) return;

      try {
        const res = await leadIntakeService.getSettings(user.uid);
        setSignatureSettings(res.settings);
      } catch (error) {
        console.error('Error fetching signature settings:', error);
      }
    };

    fetchSignatureSettings();
  }, [user?.uid]);

  // Load custom email templates
  useEffect(() => {
    const loadCustomTemplates = async () => {
      if (!user) return;
      
      setTemplatesLoading(true);
      try {
        const templates = await EmailTemplateService.getUserTemplates();
        setCustomTemplates(templates);
      } catch (error) {
        console.error('Error loading custom templates:', error);
      } finally {
        setTemplatesLoading(false);
      }
    };

    if (isOpen) {
      loadCustomTemplates();
    }
  }, [isOpen, user]);

  const [invoiceData, setInvoiceData] = useState({
    client: client?.name || getEmailFromString(email?.sender) || '',
    email: client?.email || getEmailFromString(email?.sender) || '',
    recipients: (client?.email || getEmailFromString(email?.sender)) ? 
      [{ email: client?.email || getEmailFromString(email?.sender), id: Date.now() }] : [], // Multiple recipients array
    project: project?.projectName || email?.subject || '',
    amount: '',
    // Add quantity and rate for simple mode
    quantity: 1,
    rate: '',
    currency: 'USD',
    dueDate: project?.dueDate?.toDate() || new Date(),
    sendEmail: 'auto', // 'auto', 'custom', 'none'
    projectId: project?.id || null,
    clientId: client?.id || project?.clientId || null,
    // New fields for enhanced invoice system
    lineItems: [],
    taxRate: 0,
    taxType: 'percentage',
    notes: '',
    // New field for custom payment confirmation email
    customPaymentEmail: '', 
    emailStyle: 'professional', // Added email style state
    customEmailMessage: '', // Always available for editing
    // New customization fields
    fromName: '',
    clientName: client?.name || getEmailFromString(email?.sender) || '',
    businessInfo: {},
    // Fee payer selection: 'customer' or 'creator'
    feePayer: 'creator'
  });

  // Smart email sending mode
  const getEmailMode = () => {
    const hasValidRecipients = invoiceData.recipients.some(r => r.email.trim());
    if (!hasValidRecipients && !invoiceData.email) return 'none';
    if (invoiceData.sendEmail === 'none') return 'none';
    // Default to auto mode which includes customization capability
    return 'auto';
  };

  // Show/hide currency dropdown
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const currencies = ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY', 'BMD', 'CHF', 'CNY', 'HKD', 'NZD', 'SGD'];

  // Currency symbol mapping
  const getCurrencySymbol = (currency) => {
    const symbols = {
      'USD': '$',
      'EUR': '€',
      'GBP': '£',
      'CAD': 'C$',
      'AUD': 'A$',
      'JPY': '¥',
      'BMD': 'BD$',  // Bermudian Dollar
      'CHF': 'CHF',
      'CNY': '¥',
      'HKD': 'HK$',
      'NZD': 'NZ$',
      'SGD': 'S$'
    };
    return symbols[currency] || currency;
  };

  // Helper functions for line item validation
  const isLineItemEmpty = (item) => {
    return !item.description?.trim() && !item.rate?.toString().trim();
  };

  const isLineItemValid = (item) => {
    return item.description?.trim() && item.rate?.toString().trim() && parseFloat(item.rate) !== 0;
  };

  const getValidLineItems = (lineItems) => {
    return lineItems.filter(item => !isLineItemEmpty(item));
  };

  // eslint-disable-next-line no-unused-vars
  const getIncompleteLineItems = (lineItems) => {
    const nonEmptyItems = getValidLineItems(lineItems);
    return nonEmptyItems.filter(item => !isLineItemValid(item));
  };

  // Helper function to get a proper user name for email signatures
  const getUserName = () => {
    // Try multiple sources for the user's name (prioritize profile data)
    
    // Priority 1: User's display name if explicitly set
    if (user?.displayName && user.displayName.trim()) {
      return user.displayName;
    }
    
    // Priority 2: Profile name from business info (this comes from profiles.name)
    if (invoiceData?.businessInfo?.businessName && invoiceData.businessInfo.businessName.trim()) {
      return invoiceData.businessInfo.businessName;
    }
    
    // Priority 3: Custom fromName field
    if (invoiceData?.fromName && invoiceData.fromName.trim()) {
      return invoiceData.fromName;
    }
    
    // Priority 4: Extract a reasonable name from email
    if (user?.email) {
      const emailName = user.email.split('@')[0];
      // Convert email username to a more readable format
      return emailName
        .replace(/[._-]/g, ' ')
        .replace(/\b\w/g, l => l.toUpperCase())
        .trim();
    }
    
    // Final fallback - but this should rarely happen
    return 'Your photographer';
  };

  // Sign-off templates for email signatures
  const SIGN_OFF_TEMPLATES = {
    warmly: 'Warmly,',
    best: 'Best,',
    cheers: 'Cheers,',
    thanks: 'Thanks,',
    talk_soon: 'Talk soon,',
    looking_forward: 'Looking forward to connecting,'
  };

  // Helper function to build email signature from settings
  const getEmailSignature = () => {
    const signature = signatureSettings?.signature;
    const aiSettings = signatureSettings?.aiSettings;

    // If signature is set up, use it
    if (signature?.name) {
      const signOffKey = aiSettings?.signOff || 'best';
      const signOffText = SIGN_OFF_TEMPLATES[signOffKey] || 'Best,';
      const name = signature.name;
      const title = signature.title || '';
      const business = signature.businessName && signature.businessName !== signature.name
        ? signature.businessName : '';

      let signatureBlock = `${signOffText}\n${name}`;
      if (title) signatureBlock += `\n${title}`;
      if (business) signatureBlock += `\n${business}`;

      return signatureBlock;
    }

    // Fallback to the default getUserName approach
    return `Best regards,\n${getUserName()}`;
  };

  // Calculate totals
  const subtotal = invoiceData.lineItems.length > 0
    ? invoiceData.lineItems.reduce((sum, item) => sum + (item.amount || 0), 0)
    : (parseFloat(invoiceData.quantity) || 0) * (parseFloat(invoiceData.rate) || 0);

  const taxAmount = invoiceData.taxType === 'percentage'
    ? (subtotal * (parseFloat(invoiceData.taxRate) || 0)) / 100
    : parseFloat(invoiceData.taxRate) || 0;

  const total = subtotal + taxAmount;

  // Dynamic tab configuration - skip email tab if no recipients
  const hasValidRecipients = invoiceData.recipients.some(r => r.email.trim()) || invoiceData.email;
  
  const tabs = [
    { id: 'basics', name: 'Invoice Details' },
    ...(hasValidRecipients ? [{ id: 'email', name: 'Email Settings' }] : []),
    { id: 'survey', name: 'Client Survey', beta: true },
    { id: 'review', name: 'Review & Send' }
  ];

  // Handle case where user is on email tab but removes all recipients
  useEffect(() => {
    if (activeTab === 'email' && !hasValidRecipients) {
      // If user is on email tab but no recipients exist, move to survey tab
      setActiveTab('survey');
    }
  }, [activeTab, hasValidRecipients]);

  // Helper functions remain the same...
  const getEmailTemplate = (style, clientName, projectName, currency, total, dueDate) => {
    const firstName = clientName?.split(' ')[0] || 'there';
    const emailSignature = getEmailSignature();

    if (style === 'friendly') {
      return `Hi ${firstName},

Hope you're doing well! Your invoice for "${projectName}" is ready when you are.

Amount: ${currency} ${total.toFixed(2)}
Due: ${dueDate.toLocaleDateString()}

[View Invoice Button]

Thanks so much!

${emailSignature}`;
    } else {
      // Default professional template
      return `Hi ${firstName},

Your invoice for "${projectName}" is ready for payment.

Amount: ${currency} ${total.toFixed(2)}
Due: ${dueDate.toLocaleDateString()}

[View Invoice Button]

Thank you for your business!

${emailSignature}`;
    }
  };

  const getPaymentConfirmationTemplate = (style, clientName, projectName) => {
    const firstName = clientName?.split(' ')[0] || 'there';
    const emailSignature = getEmailSignature();

    if (style === 'friendly') {
      return `Hi ${firstName},

Your payment went through perfectly! Thanks so much for working with us.

Your project "${projectName}" is all set. We really appreciate your business!

${emailSignature}`;
    } else {
      // Default professional template
      return `Hi ${firstName},

Your payment has been successfully processed. Thank you for your business.

Project: ${projectName}
Status: Payment Confirmed

${emailSignature}`;
    }
  };

  // Helper function to get default survey questions
  const getDefaultSurveyQuestions = () => {
    return [
      {
        id: '1',
        type: 'rating',
        category: 'Photography Experience',
        question: 'How would you rate your overall photography experience?',
        required: true,
        options: { scale: 5, labels: ['Poor', 'Fair', 'Good', 'Very Good', 'Excellent'] }
      },
      {
        id: '2',
        type: 'text',
        category: 'Photography Experience', 
        question: 'What did you love most about the photography process?',
        required: false,
        placeholder: 'Share what stood out to you...'
      },
      {
        id: '3',
        type: 'yes_no',
        category: 'Photo Quality & Usability',
        question: 'Are the photos usable for your intended purposes?',
        required: true
      },
      {
        id: '4',
        type: 'rating',
        category: 'Service & Communication',
        question: 'How would you rate the value for money?',
        required: true,
        options: { scale: 5, labels: ['Poor Value', 'Fair Value', 'Good Value', 'Great Value', 'Excellent Value'] }
      },
      {
        id: '5',
        type: 'text',
        category: 'Improvement & Future',
        question: 'Do you envision any alternative approaches or improvements?',
        required: false,
        placeholder: 'Any suggestions or ideas...'
      },
      {
        id: '6',
        type: 'rating',
        category: 'Improvement & Future',
        question: 'How likely are you to recommend our photography services?',
        required: true,
        options: { scale: 10, labels: ['Not Likely', 'Very Likely'] }
      }
    ];
  };

  // Custom question management
  const addCustomQuestion = () => {
    const newQuestion = {
      id: Date.now().toString(),
      type: 'text',
      category: 'Custom',
      question: '',
      required: false,
      placeholder: '',
      options: {}
    };
    setSurveySettings(prev => ({
      ...prev,
      questions: [...prev.questions, newQuestion]
    }));
  };

  const updateQuestion = (questionId, updates) => {
    setSurveySettings(prev => ({
      ...prev,
      questions: prev.questions.map(q => 
        q.id === questionId ? { ...q, ...updates } : q
      )
    }));
  };

  const removeQuestion = (questionId) => {
    setSurveySettings(prev => ({
      ...prev,
      questions: prev.questions.filter(q => q.id !== questionId)
    }));
  };

  // Recipient management functions
  const addRecipient = () => {
    if (invoiceData.recipients.length >= 5) return; // Max 5 recipients
    
    setInvoiceData(prev => ({
      ...prev,
      recipients: [...prev.recipients, { email: '', id: Date.now() }]
    }));
  };

  const updateRecipient = (id, email) => {
    setInvoiceData(prev => ({
      ...prev,
      recipients: prev.recipients.map(r => 
        r.id === id ? { ...r, email } : r
      )
    }));
    // Show dropdown when typing, hide when empty
    if (email.length > 0) {
      setActiveRecipientDropdown(id);
    } else {
      setActiveRecipientDropdown(null);
    }
  };
  
  // Handle recipient selection from customer dropdown
  const handleRecipientCustomerSelect = (recipientId, customer) => {
    console.log('handleRecipientCustomerSelect called with:', { recipientId, customer });
    
    if (!customer || !customer.email) {
      console.error('Customer or customer.email is missing', customer);
      return;
    }
    
    setInvoiceData(prev => ({
      ...prev,
      recipients: prev.recipients.map(r => 
        r.id === recipientId ? { ...r, email: customer.email } : r
      )
    }));
    setActiveRecipientDropdown(null); // Close dropdown after selection
  };

  const removeRecipient = (id) => {
    setInvoiceData(prev => ({
      ...prev,
      recipients: prev.recipients.filter(r => r.id !== id)
    }));
  };

  // All other useEffect hooks and handlers remain the same...
  // (I'll keep the existing logic but reorganize the render)

  // Initialize email templates when component loads
  useEffect(() => {
    // Only initialize if we have the required data and no existing custom message
    if (invoiceData.client && invoiceData.project && !invoiceData.customEmailMessage.trim()) {
      const template = getEmailTemplate(invoiceData.emailStyle, invoiceData.client, invoiceData.project, invoiceData.currency, total, invoiceData.dueDate);
      setInvoiceData(prev => ({ ...prev, customEmailMessage: template }));
    }
    
    // Initialize payment email template only when payment settings are shown and no existing message
    if (invoiceData.client && invoiceData.project && paymentEmailSettings.sendConfirmationEmail && !paymentEmailSettings.customMessage.trim()) {
      const template = getPaymentConfirmationTemplate(paymentEmailSettings.emailMode, invoiceData.client, invoiceData.project);
      setPaymentEmailSettings(prev => ({ ...prev, customMessage: template }));
    }
    
    // Initialize survey settings when survey is enabled
    if (invoiceData.client && invoiceData.project && surveySettings.createSurvey && !surveySettings.surveyTitle.trim()) {
      setSurveySettings(prev => ({
        ...prev,
        surveyTitle: `Feedback for ${invoiceData.project}`,
        questions: surveySettings.questions.length === 0 ? getDefaultSurveyQuestions() : prev.questions
      }));
    }
  }, [invoiceData.client, invoiceData.project, invoiceData.currency, invoiceData.dueDate, invoiceData.amount, invoiceData.lineItems, total, paymentEmailSettings.sendConfirmationEmail, surveySettings.createSurvey, invoiceData.emailStyle, paymentEmailSettings.emailMode, user?.displayName]);

  // Update email templates when key values change (even after initial setup)
  useEffect(() => {
    // Update invoice email template if it's using a default template (not deeply customized)
    if (invoiceData.client && invoiceData.project && invoiceData.customEmailMessage) {
      // Check if the current message looks like a generated template (contains [View Invoice Button])
      const isTemplateMessage = invoiceData.customEmailMessage.includes('[View Invoice Button]') || 
                               invoiceData.customEmailMessage.includes('Amount:') ||
                               invoiceData.customEmailMessage.includes('Due:');
      
      if (isTemplateMessage) {
        const newTemplate = getEmailTemplate(invoiceData.emailStyle, invoiceData.client, invoiceData.project, invoiceData.currency, total, invoiceData.dueDate);
        setInvoiceData(prev => ({ ...prev, customEmailMessage: newTemplate }));
      }
    }
    
    // Update payment email template if it's using a default template
    if (invoiceData.client && invoiceData.project && paymentEmailSettings.sendConfirmationEmail && paymentEmailSettings.customMessage) {
      const isTemplateMessage = paymentEmailSettings.customMessage.includes('payment') && 
                               paymentEmailSettings.customMessage.includes('processed');
      
      if (isTemplateMessage) {
        const newTemplate = getPaymentConfirmationTemplate(paymentEmailSettings.emailMode, invoiceData.client, invoiceData.project);
        setPaymentEmailSettings(prev => ({ ...prev, customMessage: newTemplate }));
      }
    }
  }, [total, invoiceData.currency, invoiceData.project, invoiceData.client, invoiceData.dueDate, invoiceData.emailStyle, paymentEmailSettings.emailMode, paymentEmailSettings.sendConfirmationEmail]);

  // Update invoice data when email changes
  useEffect(() => {
    if (email) {
      console.log('Email Update Effect:', {
        sender: email.sender,
        emailMatch: email.sender ? email.sender.match(/<([^>]+)>/) : null,
        extractedEmail: getEmailFromString(email.sender)
      });

      const extractedEmail = getEmailFromString(email.sender);
      console.log('Final extracted email:', extractedEmail);

      setInvoiceData(prev => {
        const newData = {
          ...prev,
          client: client?.name || extractedEmail || '',
          email: client?.email || extractedEmail || '',
          project: project?.projectName || email.subject || ''
        };
        console.log('New invoice data:', newData);
        return newData;
      });
    }
  }, [email, client, project]);

  // Load the user's services from Firestore
  useEffect(() => {
    const fetchServices = async () => {
      if (!user) return;
      const firestore = getFirestore();
      const profileRef = doc(firestore, 'profiles', user.uid);
      const profileSnapshot = await getDoc(profileRef);
      if (profileSnapshot.exists()) {
        const data = profileSnapshot.data();
        setServices(data.services || []);
      }
    };
    fetchServices();
  }, [user]);

  // Load the user's clients from Firestore
  useEffect(() => {
    if (!user) return;
    const firestore = getFirestore();
    const clientsRef = collection(firestore, 'users', user.uid, 'clients');
    const unsubscribe = onSnapshot(clientsRef, snapshot => {
      const clientsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setCustomers(clientsData);
    });
    return () => unsubscribe();
  }, [user]);

  // Load the user's email accounts
  useEffect(() => {
    const fetchAccounts = async () => {
      if (!user) return;
      try {
        console.log('Fetching email accounts for user:', user.uid);
        const accountsData = await EmailService.getAccounts(user.uid);
        console.log('Fetched accounts data:', accountsData);
        setUserAccounts(accountsData.accounts || []);
        console.log('Set user accounts:', accountsData.accounts || []);
      } catch (error) {
        console.error('Error fetching email accounts:', error);
        setUserAccounts([]);
      }
    };
    fetchAccounts();
  }, [user]);

  // Load custom email templates
  useEffect(() => {
    const loadCustomTemplates = async () => {
      if (!user) return;
      setTemplatesLoading(true);
      try {
        const templates = await EmailTemplateService.getUserTemplates();
        setCustomTemplates(templates);
      } catch (error) {
        console.error('Error loading custom templates:', error);
        setCustomTemplates([]);
      } finally {
        setTemplatesLoading(false);
      }
    };
    loadCustomTemplates();
  }, [user]);

  // Track feature usage when modal opens
  useEffect(() => {
    if (isOpen) {
      trackFeatureUsed('Invoice Creation', {
        source: project ? 'project' : email ? 'email' : 'manual',
        hasProject: !!project,
        hasClient: !!client,
        hasEmail: !!email
      });
    }
  }, [isOpen, project, client, email, trackFeatureUsed]);

  // Add click outside handler to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close client dropdown if click is outside
      if (showClientDropdown && clientDropdownRef.current && !clientDropdownRef.current.contains(event.target)) {
        setShowClientDropdown(false);
      }
      
      // Close service dropdown if click is outside
      if (showServiceDropdown && serviceDropdownRef.current && !serviceDropdownRef.current.contains(event.target)) {
        setShowServiceDropdown(false);
      }
      
      // Close recipient dropdown if click is outside
      if (activeRecipientDropdown !== null) {
        const activeRef = recipientDropdownRefs.current[activeRecipientDropdown];
        if (activeRef && !activeRef.contains(event.target)) {
          setActiveRecipientDropdown(null);
        }
      }
    };

    // Add event listener when dropdowns are open
    if (showClientDropdown || showServiceDropdown || activeRecipientDropdown !== null) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    // Clean up event listener
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showClientDropdown, showServiceDropdown, activeRecipientDropdown]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (newClientDebounceRef.current) {
        clearTimeout(newClientDebounceRef.current);
      }
    };
  }, []);

  // Close modal if not open
  if (!isOpen) return null;

  // ... (All the existing handler functions remain the same - handleChange, handleCustomerSelect, etc.)
  // I'll include all the handlers but won't repeat them here for brevity

  // Helper to update invoiceData
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Special handling for client field
    if (name === 'client') {
      // Always update the field value first
      setInvoiceData(prev => ({ ...prev, [name]: value, clientId: null }));
      
      // Reset client completion when user starts typing a new name
      if (clientCompleted) {
        setClientCompleted(false);
        setClientSaved(false);
      }
      
      // Clear any existing debounce
      if (newClientDebounceRef.current) {
        clearTimeout(newClientDebounceRef.current);
      }
      
      // Check if the value contains an email in angle brackets
      const emailMatch = value.match(/<([^>]+)>/);
      if (emailMatch) {
        // Extract the email from between angle brackets
        const email = emailMatch[1];
        // Extract the name part (everything before the <)
        const namePart = value.split('<')[0].trim();
        
        // Update both client name and email fields
        setInvoiceData(prev => ({ 
          ...prev, 
          [name]: namePart, 
          email: email
        }));
        setShowClientDropdown(false);
        setShowNewClientForm(false);
        return;
      }
      
      // Check if this client name matches any existing clients exactly
      const exactMatch = customers.find(c => 
        c.name.toLowerCase() === value.toLowerCase()
      );
      
      if (exactMatch) {
        // Auto-populate with existing client data
        setInvoiceData(prev => ({
          ...prev,
          client: exactMatch.name,
          email: exactMatch.email,
          clientId: exactMatch.id,
          sendEmail: exactMatch.email ? 'auto' : 'none'
        }));
        setShowClientDropdown(false);
        setShowNewClientForm(false);
        return;
      }
      
      // Check if there are any partial matches
      const partialMatches = customers.filter(c => 
        c.name.toLowerCase().includes(value.toLowerCase()) ||
        c.email.toLowerCase().includes(value.toLowerCase()) ||
        (c.company && c.company.toLowerCase().includes(value.toLowerCase()))
      );
      
      if (value.trim() && partialMatches.length > 0) {
        // Show dropdown with filtered results
        setShowClientDropdown(true);
        setShowNewClientForm(false);
      } else if (!value.trim()) {
        // Empty field - hide everything
        setShowClientDropdown(false);
        setShowNewClientForm(false);
      } else if (value.trim()) {
        // No matches found - this is a new client, show email form after debounce
        // Debounce the new client form to avoid showing it while user is still typing
        newClientDebounceRef.current = setTimeout(() => {
          setShowClientDropdown(false);
          setShowNewClientForm(true);
          setNewClientEmail('');
        }, 800); // Wait 800ms after user stops typing
      } else {
        // Fallback - hide everything
        setShowClientDropdown(false);
        setShowNewClientForm(false);
      }
      return;
    }
    
    // Handle amount field to prevent leading zeros
    let finalValue = value;
    if (name === 'amount') {
      // Prevent leading zeros for numeric fields (except for decimal values)
      if (typeof value === 'string') {
        // Allow empty string
        if (value === '') {
          finalValue = value;
        } else if (value === '0') {
          // Allow single zero
          finalValue = value;
        } else if (value.startsWith('0') && !value.startsWith('0.')) {
          // Remove leading zeros except for decimal values
          finalValue = value.replace(/^0+/, '') || '0';
        }
      }
    }
    
    // Default handling for other fields
    setInvoiceData(prev => ({ ...prev, [name]: finalValue }));
  };

  // If user selects a client from the list
  const handleCustomerSelect = (customer) => {
    setInvoiceData(prev => ({
      ...prev,
      client: customer.name,
      email: customer.email,
      recipients: customer.email ? [{ email: customer.email, id: Date.now() }] : prev.recipients, // Update recipients
      clientId: customer.id,
      sendEmail: customer.email ? 'auto' : 'none', // Smart default based on email availability
      notes: `Dear ${customer.name},\n\nPlease find attached the invoice for ${prev.project}.`
    }));
    setShowClientDropdown(false);
    setShowNewClientForm(false);
    setClientCompleted(true); // Mark customer selection as complete
    setClientSaved(true);
    // Auto-hide the "Saved" indicator after 3 seconds
    setTimeout(() => setClientSaved(false), 3000);
  };

  // If user selects a service from the list
  const handleServiceSelect = (service) => {
    const newData = {
      project: service.name,
      // For new quantity × rate structure, set rate instead of amount
      rate: service.price || '',
      quantity: invoiceData.quantity || 1, // Keep existing quantity or default to 1
      currency: service.currency || 'USD'
    };
    
    // Clear amount field since we're now using quantity × rate
    newData.amount = '';
    
    setInvoiceData(prev => ({ ...prev, ...newData }));
    setShowServiceDropdown(false);
  };

  // Open Stripe dashboard to fix capabilities
  const handleOpenStripeDashboard = () => {
    window.open('https://connect.stripe.com/app/express', '_blank');
  };

  // Handle new client email addition
  const handleAddNewClient = async () => {
    if (!invoiceData.client.trim()) return;
    
    const clientData = {
      name: invoiceData.client,
      email: newClientEmail,
      createdAt: new Date()
    };
    
    try {
      const firestore = getFirestore();
      const clientsRef = collection(firestore, 'users', user.uid, 'clients');
      const newClientDoc = await addDoc(clientsRef, clientData);
      
      // Update invoice data with new client
      setInvoiceData(prev => ({
        ...prev,
        email: newClientEmail,
        recipients: newClientEmail ? [{ email: newClientEmail, id: Date.now() }] : prev.recipients, // Update recipients
        clientId: newClientDoc.id,
        sendEmail: newClientEmail ? 'auto' : 'none'
      }));
      
      setShowNewClientForm(false);
      setNewClientEmail('');
      setClientCompleted(true); // Mark customer addition as complete
      setClientSaved(true);
      // Auto-hide the "Saved" indicator after 3 seconds
      setTimeout(() => setClientSaved(false), 3000);
      toast.success(`${invoiceData.client} added to your contacts!`);
    } catch (error) {
      console.error('Error adding client:', error);
      toast.error('Failed to add client');
    }
  };

  // Skip adding new client and just use email for invoice
  const handleSkipAddingClient = () => {
    setInvoiceData(prev => ({
      ...prev,
      email: newClientEmail,
      sendEmail: newClientEmail ? 'auto' : 'none'
    }));
    setShowNewClientForm(false);
    setNewClientEmail('');
  };

  // Helper function to convert HTML to plain text for textarea
  const htmlToPlainText = (html) => {
    if (!html) return '';
    
    return html
      // Replace <p> tags with line breaks
      .replace(/<p[^>]*>/gi, '')
      .replace(/<\/p>/gi, '\n')
      // Replace <br> tags with line breaks
      .replace(/<br\s*\/?>/gi, '\n')
      // Remove other HTML tags
      .replace(/<[^>]*>/g, '')
      // Clean up multiple line breaks
      .replace(/\n\s*\n\s*\n/g, '\n\n')
      // Trim whitespace
      .trim();
  };

  // Helper function to replace variables in plain text (without HTML conversion)
  const replaceVariablesPlainText = (templateContent, variables) => {
    let processedContent = templateContent;
    
    // Replace each variable with its value
    Object.entries(variables).forEach(([key, value]) => {
      const regex = new RegExp(`\\{${key}\\}`, 'g');
      processedContent = processedContent.replace(regex, value || '');
    });
    
    return processedContent;
  };

  // Update email template when style changes
  const handleEmailStyleChange = (newStyle, customTemplate = null) => {
    let newTemplate;
    
    if (customTemplate) {
      // Use custom template with variable replacement (plain text for textarea)
      const templateVariables = {
        clientName: invoiceData.client,
        firstName: invoiceData.client?.split(' ')[0] || 'there',
        projectName: invoiceData.project,
        amount: `${invoiceData.currency} ${total.toFixed(2)}`,
        totalAmount: `${invoiceData.currency} ${total.toFixed(2)}`,
        dueDate: invoiceData.dueDate?.toLocaleDateString(),
        currency: invoiceData.currency,
        userName: getUserName(),
        businessName: invoiceData.businessInfo?.businessName || getUserName(),
        invoiceNumber: 'INV-' + Date.now().toString().slice(-6),
        currentDate: new Date().toLocaleDateString(),
        paymentLink: '[View Invoice Button]'
      };
      
      // Replace variables without HTML conversion
      newTemplate = replaceVariablesPlainText(customTemplate.content, templateVariables);
      
      // If the template has HTML tags, convert to plain text
      if (newTemplate.includes('<p>') || newTemplate.includes('<br>')) {
        newTemplate = htmlToPlainText(newTemplate);
      }
      
      // Increment usage count for the custom template
      EmailTemplateService.incrementUsage(customTemplate.id).catch(console.error);
    } else {
      // Use built-in template
      newTemplate = getEmailTemplate(newStyle, invoiceData.client, invoiceData.project, invoiceData.currency, total, invoiceData.dueDate);
    }
    
    setInvoiceData(prev => ({
      ...prev,
      emailStyle: newStyle,
      customEmailMessage: newTemplate
    }));
  };

  // Update payment email template when style changes  
  const handlePaymentEmailStyleChange = (newStyle) => {
    const newTemplate = getPaymentConfirmationTemplate(newStyle, invoiceData.client, invoiceData.project);
    setPaymentEmailSettings(prev => ({
      ...prev,
      emailMode: newStyle,
      customMessage: newTemplate
    }));
  };

  // Render tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'basics':
        return renderBasicsTab();
      case 'email':
        return renderEmailTab();
      case 'survey':
        return renderSurveyTab();
      case 'review':
        return renderReviewTab();
      default:
        return renderBasicsTab();
    }
  };

  // ... (I'll continue with the tab render functions in the next part)
  
  // Render Basics Tab
  const renderBasicsTab = () => (
    <div className="space-y-6">
          {/* Client & Project - Enhanced */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Client - Enhanced with email display and edit capability */}
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-900 dark:text-white">Client</label>
              
              {/* Show selected client with email or input mode */}
              {clientCompleted && !isEditingClient && !showNewClientForm ? (
                <div className="relative">
                  <div className="bg-gray-50 dark:bg-[#111111] rounded-xl px-3 py-2.5 border border-gray-200 dark:border-[#2A2A2A] min-h-[44px] flex items-center justify-between group hover:border-gray-300 dark:hover:border-[#666666] transition-all">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="text-gray-900 dark:text-white font-medium text-sm truncate">
                            {invoiceData.client || 'Client'}
                          </div>
                          {invoiceData.email && (
                            <div className="text-gray-500 dark:text-[#666666] text-xs truncate mt-0.5">
                              {invoiceData.email}
                            </div>
                          )}
                        </div>
                        {clientSaved && (
                          <div className="flex items-center gap-1 text-green-600 dark:text-green-400 flex-shrink-0">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                            </svg>
                            <span className="text-xs font-medium">Saved</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setIsEditingClient(true)}
                      className="flex-shrink-0 ml-2 p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-[#1A1A1A] transition-colors opacity-0 group-hover:opacity-100"
                      title="Edit client details"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                  </div>
                </div>
              ) : (
                <div className="relative" ref={clientDropdownRef}>
                  <input
                    type="text"
                    name="client"
                    className="w-full bg-gray-50 dark:bg-[#111111] rounded-xl px-3 py-2.5 border border-gray-200 dark:border-[#2A2A2A] focus:ring-2 focus:ring-brand/20 focus:border-brand text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-[#666666]"
                    placeholder="Client name"
                    value={invoiceData.client}
                    onChange={handleChange}
                    onClick={() => !client && setShowClientDropdown(true)}
                    readOnly={!!client}
                    autoComplete="off"
                  />
                  {!client && showClientDropdown && (
                    <div className="absolute z-10 w-full mt-1 bg-white dark:bg-[#111111] rounded-2xl shadow-lg max-h-60 overflow-y-auto border border-gray-200 dark:border-[#2A2A2A]">
                      <CustomerList 
                        customers={customers} 
                        onSelect={handleCustomerSelect} 
                        searchQuery={invoiceData.client}
                      />
                    </div>
                  )}
                </div>
              )}
              
              {/* Edit Mode for Client */}
              {isEditingClient && (
                <div className="mt-2 bg-white dark:bg-black border border-brand rounded-xl p-4 shadow-lg">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-medium text-gray-900 dark:text-white">Edit Client Details</h4>
                      <button
                        type="button"
                        onClick={() => setIsEditingClient(false)}
                        className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    
                    <div className="space-y-2">
                      <input
                        type="text"
                        placeholder="Client name"
                        value={invoiceData.client}
                        onChange={(e) => setInvoiceData(prev => ({ ...prev, client: e.target.value }))}
                        className="w-full px-3 py-2 bg-gray-50 dark:bg-[#111111] rounded-lg border border-gray-200 dark:border-[#2A2A2A] text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-[#666666] focus:ring-2 focus:ring-brand/20 focus:border-brand"
                      />
                      <input
                        type="email"
                        placeholder="Client email"
                        value={invoiceData.email}
                        onChange={(e) => setInvoiceData(prev => ({ ...prev, email: e.target.value }))}
                        className="w-full px-3 py-2 bg-gray-50 dark:bg-[#111111] rounded-lg border border-gray-200 dark:border-[#2A2A2A] text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-[#666666] focus:ring-2 focus:ring-brand/20 focus:border-brand"
                      />
                    </div>
                    
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setIsEditingClient(false);
                          setClientSaved(true);
                          // Auto-hide the "Saved" indicator after 3 seconds
                          setTimeout(() => setClientSaved(false), 3000);
                        }}
                        className="flex-1 px-3 py-2 bg-brand hover:bg-brand-hover text-white text-sm font-medium rounded-lg transition-colors"
                      >
                        Save Changes
                      </button>
                      <button
                        type="button"
                        onClick={() => setIsEditingClient(false)}
                        className="px-3 py-2 bg-gray-100 dark:bg-[#1A1A1A] hover:bg-gray-200 dark:hover:bg-[#2A2A2A] text-gray-700 dark:text-[#666666] text-sm font-medium rounded-lg transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Project */}
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-900 dark:text-white">Project</label>
              <div className="relative" ref={serviceDropdownRef}>
                <input
                  type="text"
                  name="project"
                  className="w-full bg-gray-50 dark:bg-[#111111] rounded-xl px-3 py-2.5 border border-gray-200 dark:border-[#2A2A2A] focus:ring-2 focus:ring-brand/20 focus:border-brand text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-[#666666]"
                  placeholder="Project name"
                  value={invoiceData.project}
                  onChange={handleChange}
                  onClick={() => !project && setShowServiceDropdown(true)}
                  readOnly={!!project}
                  autoComplete="off"
                />
                {!project && showServiceDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white dark:bg-[#111111] rounded-2xl shadow-lg max-h-60 overflow-y-auto border border-gray-200 dark:border-[#2A2A2A]">
                    <ServiceList services={services} onSelect={handleServiceSelect} />
                  </div>
                )}
              </div>
            </div>
          </div>
            
          {/* New Client Form */}
          {showNewClientForm && (
            <div className="p-3 bg-gray-50 dark:bg-[#111111] border border-gray-200 dark:border-[#2A2A2A] rounded-xl">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-gray-500 dark:bg-[#666666] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                    Email for "{invoiceData.client}"
                  </h4>
                  <p className="text-xs text-gray-600 dark:text-[#666666] mb-2">
                    Will be added to your contacts for future invoices
                  </p>
                  
                  <div className="space-y-2">
                    <input
                      type="email"
                      placeholder="Client's email address"
                      value={newClientEmail}
                      onChange={(e) => setNewClientEmail(e.target.value)}
                      className="w-full px-3 py-2 bg-white dark:bg-black rounded-lg border border-gray-200 dark:border-[#2A2A2A] text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-[#666666] focus:ring-2 focus:ring-brand/20 focus:border-brand"
                      autoComplete="off"
                    />
                    
                    <div className="flex items-center justify-between">
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={handleAddNewClient}
                          className="px-3 py-1.5 bg-brand hover:bg-brand-hover text-white text-xs font-medium rounded-lg transition-colors"
                        >
                          Continue
                        </button>
                        <button
                          type="button"
                          onClick={handleSkipAddingClient}
                          className="px-3 py-1.5 bg-gray-100 dark:bg-[#1A1A1A] hover:bg-gray-200 dark:hover:bg-[#2A2A2A] text-gray-700 dark:text-[#666666] text-xs font-medium rounded-lg transition-colors"
                        >
                          Don't Save
                        </button>
                      </div>
                      
                      <button
                        type="button"
                        onClick={() => {
                          setShowNewClientForm(false);
                          setNewClientEmail('');
                        }}
                        className="px-3 py-1.5 bg-gray-100 dark:bg-[#1A1A1A] hover:bg-gray-200 dark:hover:bg-[#2A2A2A] text-gray-700 dark:text-[#666666] text-xs font-medium rounded-lg transition-colors"
                      >
                        Skip Email
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

      {/* Unified Pricing */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-900 dark:text-white">Pricing</label>

            {invoiceData.lineItems.length > 0 ? (
              <div className="space-y-3">
                <LineItemsManager
                  lineItems={invoiceData.lineItems}
                  setLineItems={(items) => setInvoiceData(prev => ({ ...prev, lineItems: items }))}
                  currency={invoiceData.currency}
                />
                
                <div className="flex justify-center gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      const total = invoiceData.lineItems.reduce((sum, item) => sum + (item.amount || 0), 0);
                      setInvoiceData(prev => ({
                        ...prev,
                        lineItems: [],
                        amount: total > 0 ? total.toString() : ''
                      }));
                    }}
                    className="px-4 py-2 text-sm font-medium text-brand hover:text-brand-hover bg-brand/10 hover:bg-brand/20 border border-brand/30 rounded-xl transition-colors flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                    </svg>
                    Switch to Simple Amount
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => {
                      const newLineItem = {
                        id: Date.now().toString(),
                        description: '',
                        quantity: 1,
                        rate: '',
                        amount: 0
                      };
                      setInvoiceData(prev => ({
                        ...prev,
                        lineItems: [...prev.lineItems, newLineItem]
                      }));
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-[#666666] hover:text-gray-900 dark:hover:text-white bg-gray-50 dark:bg-[#111111] hover:bg-gray-100 dark:hover:bg-[#1A1A1A] border border-gray-200 dark:border-[#2A2A2A] rounded-xl transition-colors flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Add Another Item
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                  {/* Quantity Field */}
                  <div className="sm:col-span-1 space-y-1">
                    <label className="block text-sm font-medium text-gray-900 dark:text-white">Quantity</label>
                    <input
                      type="number"
                      name="quantity"
                      className="w-full bg-gray-50 dark:bg-[#111111] rounded-xl px-3 py-2.5 border border-gray-200 dark:border-[#2A2A2A] focus:ring-2 focus:ring-brand/20 focus:border-brand text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-[#666666] font-medium text-center hover:border-gray-300 dark:hover:border-[#666666] transition-all [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      placeholder="1"
                      value={invoiceData.quantity}
                      onChange={(e) => setInvoiceData(prev => ({ ...prev, quantity: e.target.value }))}
                      step="0.01"
                      min="0"
                      autoComplete="off"
                    />
                  </div>

                  {/* Rate Field */}
                  <div className="sm:col-span-2 space-y-1">
                    <label className="block text-sm font-medium text-gray-900 dark:text-white">Rate</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                        <span className="text-gray-500 dark:text-[#666666] font-medium text-sm">{invoiceData.currency}</span>
                      </div>
                      <input
                        type="number"
                        name="rate"
                        className="w-full bg-gray-50 dark:bg-[#111111] rounded-xl pl-12 pr-3 py-2.5 border border-gray-200 dark:border-[#2A2A2A] focus:ring-2 focus:ring-brand/20 focus:border-brand text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-[#666666] font-medium text-lg hover:border-gray-300 dark:hover:border-[#666666] transition-all [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        placeholder="0.00"
                        value={invoiceData.rate}
                        onChange={(e) => setInvoiceData(prev => ({ ...prev, rate: e.target.value }))}
                        step="0.01"
                        min="0"
                        autoComplete="off"
                      />
                    </div>
                  </div>

                  {/* Currency Dropdown */}
                  <div className="sm:col-span-1 relative space-y-1">
                    <label className="block text-sm font-medium text-gray-900 dark:text-white">Currency</label>
                    <button
                      type="button"
                      onClick={() => setDropdownVisible(!dropdownVisible)}
                      className="w-full h-[42px] bg-gray-50 dark:bg-[#111111] rounded-xl px-3 py-2.5 text-left flex items-center justify-between border border-gray-200 dark:border-[#2A2A2A] text-gray-900 dark:text-white hover:border-gray-300 dark:hover:border-[#666666] transition-all group"
                    >
                      <span className="font-medium text-sm">{invoiceData.currency}</span>
                      <svg className={`w-4 h-4 text-gray-400 dark:text-[#666666] group-hover:text-brand transition-all duration-200 ${dropdownVisible ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {dropdownVisible && (
                      <div className="absolute w-full bg-white dark:bg-[#111111] shadow-xl rounded-xl mt-1 py-2 z-10 border border-gray-200 dark:border-[#2A2A2A] backdrop-blur-sm">
                        {currencies.map((cur) => (
                          <button
                            key={cur}
                            type="button"
                            className="w-full px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-[#1A1A1A] transition-colors text-gray-900 dark:text-white group flex items-center justify-between"
                            onClick={() => {
                              setInvoiceData(prev => ({ ...prev, currency: cur }));
                              setDropdownVisible(false);
                            }}
                          >
                            <span className="font-medium text-sm">{cur}</span>
                            {cur === invoiceData.currency && (
                              <svg className="w-4 h-4 text-brand" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex justify-center">
                  <button
                    type="button"
                    onClick={() => {
                      const lineItems = [];
                      
                      // Convert simple mode to line items
                      if (invoiceData.rate && invoiceData.quantity) {
                        lineItems.push({
                          id: Date.now().toString(),
                          description: invoiceData.project || 'Service',
                          quantity: parseFloat(invoiceData.quantity) || 1,
                          rate: parseFloat(invoiceData.rate) || 0,
                          amount: (parseFloat(invoiceData.quantity) || 1) * (parseFloat(invoiceData.rate) || 0)
                        });
                      }
                      
                      lineItems.push({
                        id: (Date.now() + 1).toString(),
                        description: '',
                        quantity: 1,
                        rate: '',
                        amount: 0
                      });
                      
                      setInvoiceData(prev => ({
                        ...prev,
                        lineItems: lineItems,
                        amount: '',
                        quantity: 1,
                        rate: ''
                      }));
                    }}
                    className="px-4 py-2 text-sm font-medium text-brand hover:text-brand-hover bg-brand/10 hover:bg-brand/20 border border-brand/30 rounded-xl transition-colors flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Add More Line Items
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Tax Calculator */}
          <TaxCalculator
            subtotal={subtotal}
            taxRate={invoiceData.taxRate}
            taxType={invoiceData.taxType}
            onTaxRateChange={(rate) => setInvoiceData(prev => ({ ...prev, taxRate: rate }))}
            onTaxTypeChange={(type) => setInvoiceData(prev => ({ ...prev, taxType: type }))}
            currency={invoiceData.currency}
          />

          {/* Transaction Fee Payer */}
          {/* a toggle to switch between creator and customer paying the fee */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-900 dark:text-white">Transaction Fee (2.9%)</label>
            <p className="text-xs text-gray-500 dark:text-[#666666] mb-3">By default, you pay the fee. You can choose to pass it to the customer.</p>
                          <div className="flex gap-3">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="button"
                  onClick={() => setInvoiceData(prev => ({ ...prev, feePayer: 'creator' }))}
                  className={`flex-1 p-3 rounded-xl border-2 transition-all duration-200 ${
                    invoiceData.feePayer === 'creator'
                      ? 'border-brand bg-brand/10 text-gray-900 dark:text-white'
                      : 'border-gray-200 dark:border-[#2A2A2A] hover:border-gray-300 dark:hover:border-[#333333] text-gray-600 dark:text-[#666666]'
                  }`}
                >
                  <div className="text-center">
                    <div className="font-medium text-sm">You Pay (Default)</div>
                    <div className="text-xs mt-1 opacity-80">Fee deducted from payment</div>
                  </div>
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="button"
                  onClick={() => setInvoiceData(prev => ({ ...prev, feePayer: 'customer' }))}
                  className={`flex-1 p-3 rounded-xl border-2 transition-all duration-200 ${
                    invoiceData.feePayer === 'customer'
                      ? 'border-brand bg-brand/10 text-gray-900 dark:text-white'
                      : 'border-gray-200 dark:border-[#2A2A2A] hover:border-gray-300 dark:hover:border-[#333333] text-gray-600 dark:text-[#666666]'
                  }`}
                >
                  <div className="text-center">
                    <div className="font-medium text-sm">Customer Pays</div>
                    <div className="text-xs mt-1 opacity-80">Fee added to invoice total</div>
                  </div>
                </motion.button>
              </div>
          </div>

      {/* Due Date */}
          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-900 dark:text-white">Due Date</label>
            <DatePicker
              selected={invoiceData.dueDate}
              onChange={(date) => setInvoiceData(prev => ({ ...prev, dueDate: date }))}
              className="w-full bg-gray-50 dark:bg-[#111111] rounded-xl px-3 py-2.5 border border-gray-200 dark:border-[#2A2A2A] focus:ring-2 focus:ring-brand/20 focus:border-brand text-gray-900 dark:text-white"
              dateFormat="MMMM d, yyyy"
            />
          </div>
    </div>
  );

  // Continue with other render functions...
  const renderEmailTab = () => {
    // Safety check - if no recipients, show helpful message
    if (!hasValidRecipients) {
      return (
        <div className="text-center py-12">
          <svg className="w-12 h-12 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No Email Recipients</h3>
          <p className="text-gray-500 dark:text-[#666666] mb-4">
            Add email recipients in the Invoice Details tab to configure email settings.
          </p>
          <button
            onClick={() => setActiveTab('basics')}
            className="px-4 py-2 bg-brand hover:bg-brand-hover text-white text-sm font-medium rounded-lg transition-colors"
          >
            Go Back to Invoice Details
          </button>
        </div>
      );
    }

    return (
    <div className="space-y-6">
      {/* Multiple Recipients Manager */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Email Recipients</h3>
            <p className="text-sm text-gray-500 dark:text-[#666666]">Add up to 5 email addresses to receive this invoice</p>
          </div>
          {invoiceData.recipients.length < 5 && (
            <button
              type="button"
              onClick={addRecipient}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-brand hover:text-brand-hover bg-brand/10 hover:bg-brand/20 border border-brand/30 rounded-lg transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Add Email
            </button>
          )}
        </div>
        
        <div className="space-y-3">
          {invoiceData.recipients.map((recipient, index) => (
            <div 
              key={recipient.id} 
              className="relative"
              ref={el => recipientDropdownRefs.current[recipient.id] = el}
            >
              <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-[#0A0A0A] rounded-lg border border-gray-200 dark:border-[#1A1A1A]">
                <div className="flex-shrink-0 w-6 h-6 bg-brand/20 rounded-full flex items-center justify-center">
                  <span className="text-xs font-medium text-brand">{index + 1}</span>
                </div>
                <input
                  type="email"
                  placeholder="recipient@example.com"
                  value={recipient.email}
                  onChange={(e) => updateRecipient(recipient.id, e.target.value)}
                  onFocus={() => setActiveRecipientDropdown(recipient.id)}
                  className="flex-1 px-3 py-2 bg-white dark:bg-black rounded-lg border border-gray-200 dark:border-[#2A2A2A] text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-[#666666] focus:ring-2 focus:ring-brand/20 focus:border-brand"
                />
                {invoiceData.recipients.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeRecipient(recipient.id)}
                    className="flex-shrink-0 p-1.5 text-gray-400 hover:text-red-500 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                )}
              </div>
              {/* Customer dropdown for recipient field */}
              {activeRecipientDropdown === recipient.id && (
                <div className="absolute z-10 w-full mt-1 bg-white dark:bg-[#111111] rounded-2xl shadow-lg max-h-60 overflow-y-auto border border-gray-200 dark:border-[#2A2A2A]">
                  <CustomerList 
                    customers={customers} 
                    onSelect={(customer) => {
                      console.log('Selected customer:', customer); // Debug log
                      handleRecipientCustomerSelect(recipient.id, customer);
                    }} 
                    searchQuery={recipient.email}
                  />
                </div>
              )}
            </div>
          ))}
          
          {invoiceData.recipients.length === 0 && (
            <div className="text-center py-8 text-gray-500 dark:text-[#666666]">
              <svg className="w-8 h-8 mx-auto mb-2 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <p className="text-sm">No recipients added yet</p>
              <button
                type="button"
                onClick={addRecipient}
                className="mt-2 text-sm font-medium text-brand hover:text-brand-hover transition-colors"
              >
                Add your first recipient
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Invoice Email Settings */}
          {(invoiceData.recipients.some(r => r.email.trim()) || invoiceData.email) && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 bg-brand/10 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-brand" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Invoice Email Delivery</h3>
              <p className="text-sm text-gray-500 dark:text-[#666666]">
                Send instant email to {
                  invoiceData.recipients.filter(r => r.email.trim()).length > 1 
                    ? `${invoiceData.recipients.filter(r => r.email.trim()).length} recipients`
                    : invoiceData.recipients.find(r => r.email.trim())?.email || invoiceData.email
                }
              </p>
                </div>
              </div>

          {/* Email Options */}
              <div className="grid gap-3">
                <div 
                  onClick={() => setInvoiceData(prev => ({ ...prev, sendEmail: 'auto' }))}
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 group ${
                    invoiceData.sendEmail === 'auto' 
                      ? 'border-brand bg-brand/5 shadow-sm' 
                      : 'border-gray-200 dark:border-[#2A2A2A] hover:border-brand/40 hover:bg-brand/2'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                      invoiceData.sendEmail === 'auto' 
                        ? 'border-brand bg-brand' 
                        : 'border-gray-300 dark:border-[#666666] group-hover:border-brand/60'
                    }`}>
                      {invoiceData.sendEmail === 'auto' && (
                        <div className="w-2 h-2 rounded-full bg-black" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 dark:text-white">Yes, Send Email</span>
                        <span className="px-2 py-0.5 bg-brand/20 text-brand text-xs font-medium rounded-full">Recommended</span>
                      </div>
                  <p className="text-sm text-gray-500 dark:text-[#666666] mt-0.5">Send professional email instantly (customize below)</p>
                    </div>
                  </div>
                </div>

                <div 
                  onClick={() => setInvoiceData(prev => ({ ...prev, sendEmail: 'none' }))}
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 group ${
                    invoiceData.sendEmail === 'none' 
                      ? 'border-brand bg-brand/5 shadow-sm' 
                      : 'border-gray-200 dark:border-[#2A2A2A] hover:border-brand/40 hover:bg-brand/2'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                      invoiceData.sendEmail === 'none' 
                        ? 'border-brand bg-brand' 
                        : 'border-gray-300 dark:border-[#666666] group-hover:border-brand/60'
                    }`}>
                      {invoiceData.sendEmail === 'none' && (
                        <div className="w-2 h-2 rounded-full bg-black" />
                      )}
                    </div>
                    <div className="flex-1">
                        <span className="font-medium text-gray-900 dark:text-white">No, Just Create Invoice</span>
                      <p className="text-sm text-gray-500 dark:text-[#666666] mt-0.5">Create invoice only, send the link manually later</p>
                    </div>
                  </div>
                </div>
              </div>

          {/* Email Customization */}
              {invoiceData.sendEmail === 'auto' && (
                <div className="space-y-6">
                  {/* Email Style Selection */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <label className="block text-sm font-medium text-gray-900 dark:text-white">Email Style Template</label>
                      <button
                        onClick={() => setShowTemplateManager(true)}
                        className="text-sm text-brand hover:text-[#E6A15E] transition-colors"
                      >
                        Manage Templates
                      </button>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {/* Built-in Templates */}
                      {[
                        {
                          id: 'professional',
                          title: 'Professional',
                          description: 'Clean, business-focused tone',
                          icon: '🏢',
                          recommended: true
                        },
                        {
                          id: 'friendly',
                          title: 'Friendly',
                          description: 'Warm, personal approach',
                          icon: '😊'
                        }
                      ].map((style) => (
                        <motion.div
                          key={style.id}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => handleEmailStyleChange(style.id)}
                          className={`relative p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                            (invoiceData.emailStyle || 'professional') === style.id
                              ? 'border-brand bg-brand/5 dark:bg-brand/10'
                              : 'border-gray-200 dark:border-[#1A1A1A] hover:border-gray-300 dark:hover:border-[#333333] bg-white dark:bg-[#111111]'
                          }`}
                        >
                          {style.recommended && (
                            <div className="absolute -top-2 -right-2">
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-brand text-white">
                                Recommended
                              </span>
                            </div>
                          )}
                          <div className="text-2xl mb-2">{style.icon}</div>
                          <h4 className="font-semibold text-gray-900 dark:text-white text-sm mb-1">{style.title}</h4>
                          <p className="text-xs text-gray-600 dark:text-[#666666]">{style.description}</p>
                        </motion.div>
                      ))}
                      
                      {/* Custom Templates */}
                      {customTemplates.map((template) => (
                        <motion.div
                          key={template.id}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => handleEmailStyleChange(template.id, template)}
                          className={`relative p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                            invoiceData.emailStyle === template.id
                              ? 'border-brand bg-brand/5 dark:bg-brand/10'
                              : 'border-gray-200 dark:border-[#1A1A1A] hover:border-gray-300 dark:hover:border-[#333333] bg-white dark:bg-[#111111]'
                          }`}
                        >
                          <div className="absolute -top-2 -right-2">
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200">
                              Custom
                            </span>
                          </div>
                          <div className="text-2xl mb-2">✨</div>
                          <h4 className="font-semibold text-gray-900 dark:text-white text-sm mb-1">{template.name}</h4>
                          <p className="text-xs text-gray-600 dark:text-[#666666]">
                            {template.description || 'Your custom template'}
                          </p>
                          <div className="mt-2 text-xs text-gray-500 dark:text-[#666666]">
                            Used {template.usageCount || 0} times
                          </div>
                        </motion.div>
                      ))}
                      
                      {/* Loading State for Custom Templates */}
                      {templatesLoading && (
                        <div className="relative p-4 rounded-xl border-2 border-gray-200 dark:border-[#1A1A1A] bg-white dark:bg-[#111111] flex items-center justify-center">
                          <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-brand"></div>
                        </div>
                      )}
                      
                      {/* Create Template Button */}
                      <motion.div
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setShowTemplateManager(true)}
                        className="p-4 rounded-xl border-2 border-dashed border-gray-300 dark:border-[#333333] cursor-pointer transition-all duration-200 hover:border-brand hover:bg-brand/5 dark:hover:bg-brand/10"
                      >
                        <div className="flex items-center justify-center mb-2">
                          <div className="text-2xl">➕</div>
                        </div>
                        <h4 className="font-semibold text-gray-900 dark:text-white text-sm mb-1 text-center">Create Custom</h4>
                        <p className="text-xs text-gray-600 dark:text-[#666666] text-center">Build your own template</p>
                      </motion.div>
                    </div>
                  </div>

              {/* Custom Message Editor */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-900 dark:text-white">
                      Customize Your Message
                    </label>
                    <div className="relative">
                      <textarea
                        value={invoiceData.customEmailMessage || ''}
                        onChange={(e) => setInvoiceData(prev => ({ ...prev, customEmailMessage: e.target.value }))}
                        placeholder="Write your custom invoice email message..."
                        className="w-full px-4 py-3 bg-gray-50 dark:bg-[#0A0A0A] border border-gray-200 dark:border-[#1A1A1A] rounded-xl focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-[#666666] transition-all duration-200 resize-none"
                        rows="8"
                      />
                      <div className="absolute bottom-3 right-3 text-xs text-gray-400 dark:text-[#666666]">
                        {(invoiceData.customEmailMessage || '').length}/2000
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-[#666666]">
                      Use [View Invoice Button] to automatically insert the payment button
                    </p>
                  </div>

                  {/* Live Preview */}
                  <div className="p-4 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-[#0A0A0A] dark:to-[#111111] rounded-xl border border-gray-200 dark:border-[#1A1A1A]">
                    <div className="flex items-center gap-2 mb-3">
                      <svg className="w-4 h-4 text-brand" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      <span className="text-xs font-medium text-gray-900 dark:text-white">Email Preview</span>
                    </div>
                    <div className="bg-white dark:bg-[#1A1A1A] rounded-lg p-4 border border-gray-200 dark:border-[#333333]">
                      <div className="text-xs text-gray-600 dark:text-[#666666] mb-2">
                        Subject: Invoice for {invoiceData.project || 'Your Project'}
                      </div>
                      <div className="text-sm text-gray-900 dark:text-white whitespace-pre-line">
                        {invoiceData.customEmailMessage 
                          ? invoiceData.customEmailMessage.replace('[View Invoice Button]', '→ View & Pay Invoice ←')
                          : 'Your custom message will appear here...'
                        }
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

      {/* Payment Confirmation Email */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-white">Payment Confirmation Email</label>
            <p className="text-xs text-gray-500 dark:text-[#666666] mt-0.5">Sent automatically when payment is received</p>
              </div>
            </div>

                <motion.div
                  whileHover={{ scale: 1.01 }}
                  className="relative p-6 bg-white dark:bg-[#111111] border border-gray-200 dark:border-[#1A1A1A] rounded-2xl shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer"
                  onClick={() => setPaymentEmailSettings(prev => ({ ...prev, sendConfirmationEmail: !prev.sendConfirmationEmail }))}
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 mt-1">
                      <div className={`w-5 h-5 rounded-md border-2 transition-all duration-200 flex items-center justify-center ${
                        paymentEmailSettings.sendConfirmationEmail 
                          ? 'bg-brand border-brand' 
                          : 'border-gray-300 dark:border-[#333333]'
                      }`}>
                        {paymentEmailSettings.sendConfirmationEmail && (
                          <motion.svg
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="w-3 h-3 text-black"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </motion.svg>
                        )}
                      </div>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                        Send Payment Confirmation Email
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-[#666666] leading-relaxed">
                        Automatically email your customer when their payment is processed. Perfect for sharing delivery links, 
                        download instructions, next steps, or a personalized thank you message.
                      </p>
                    </div>
                  </div>
                </motion.div>

        {/* Payment Email Configuration */}
                <AnimatePresence>
                  {paymentEmailSettings.sendConfirmationEmail && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                      className="space-y-4"
                    >
              {/* Style Selection */}
                      <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-900 dark:text-white">Email Style</label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {[
                    { id: 'professional', title: 'Professional', icon: '🏢' },
                    { id: 'friendly', title: 'Friendly', icon: '😊' }
                          ].map((style) => (
                            <motion.div
                              key={style.id}
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                              onClick={() => handlePaymentEmailStyleChange(style.id)}
                      className={`p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                                paymentEmailSettings.emailMode === style.id
                                  ? 'border-brand bg-brand/5 dark:bg-brand/10'
                                  : 'border-gray-200 dark:border-[#1A1A1A] hover:border-gray-300 dark:hover:border-[#333333]'
                              }`}
                            >
                              <div className="text-2xl mb-2">{style.icon}</div>
                      <h4 className="font-semibold text-gray-900 dark:text-white text-sm">{style.title}</h4>
                            </motion.div>
                          ))}
                        </div>
                      </div>

              {/* Custom Message */}
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-900 dark:text-white">
                          Customize Confirmation Message
                        </label>
                          <textarea
                            value={paymentEmailSettings.customMessage}
                            onChange={(e) => setPaymentEmailSettings(prev => ({ ...prev, customMessage: e.target.value }))}
                  placeholder="Write your custom payment confirmation message..."
                            className="w-full px-4 py-3 bg-gray-50 dark:bg-[#0A0A0A] border border-gray-200 dark:border-[#1A1A1A] rounded-xl focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-[#666666] transition-all duration-200 resize-none"
                            rows="6"
                          />
                      </div>

              {/* Options */}
                        <motion.div
                          whileHover={{ scale: 1.01 }}
                          onClick={() => setPaymentEmailSettings(prev => ({ ...prev, notifyCreator: !prev.notifyCreator }))}
                          className="flex items-center justify-between p-4 bg-gray-50 dark:bg-[#0A0A0A] rounded-xl cursor-pointer hover:bg-gray-100 dark:hover:bg-[#111111] transition-colors duration-200"
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-4 h-4 rounded border-2 transition-all duration-200 flex items-center justify-center ${
                              paymentEmailSettings.notifyCreator 
                                ? 'bg-brand border-brand' 
                                : 'border-gray-300 dark:border-[#333333]'
                            }`}>
                              {paymentEmailSettings.notifyCreator && (
                                <svg className="w-2.5 h-2.5 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </div>
                            <div>
                              <span className="text-sm font-medium text-gray-900 dark:text-white">
                                Notify me when payment is received
                              </span>
                              <p className="text-xs text-gray-600 dark:text-[#666666]">
                                Get an instant notification email when your customer pays
                              </p>
                            </div>
                          </div>
                        </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
                      </div>
    </div>
    );
  };

  const renderSurveyTab = () => (
    <div className="space-y-6">
      {/* Survey Toggle */}
      <motion.div
        whileHover={{ scale: 1.01 }}
        className="relative p-6 bg-white dark:bg-[#111111] border border-gray-200 dark:border-[#1A1A1A] rounded-2xl shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer"
        onClick={() => setSurveySettings(prev => ({ ...prev, createSurvey: !prev.createSurvey }))}
      >
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 mt-1">
            <div className={`w-6 h-6 rounded-md border-2 transition-all duration-200 flex items-center justify-center ${
              surveySettings.createSurvey 
                ? 'bg-brand border-brand' 
                : 'border-gray-300 dark:border-[#333333]'
            }`}>
              {surveySettings.createSurvey && (
                <motion.svg
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="w-4 h-4 text-black"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </motion.svg>
              )}
            </div>
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h4 className="text-xl font-semibold text-gray-900 dark:text-white">
                Create Client Feedback Survey
              </h4>
              <span className="px-2.5 py-1 bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 text-xs font-bold rounded-lg">
                BETA
              </span>
            </div>
            <p className="text-sm text-gray-600 dark:text-[#666666] leading-relaxed">
              Create a feedback survey that will be available to your client immediately after they complete payment. 
              Gather valuable insights about their experience with your photography service.
            </p>
          </div>
        </div>
      </motion.div>

      {/* Survey Configuration */}
      <AnimatePresence>
        {surveySettings.createSurvey && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="space-y-6"
          >
            {/* Survey Title */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-900 dark:text-white">
                Survey Title
              </label>
              <input
                type="text"
                value={surveySettings.surveyTitle}
                onChange={(e) => setSurveySettings(prev => ({ ...prev, surveyTitle: e.target.value }))}
                placeholder="e.g., Feedback for Wedding Photography"
                className="w-full px-4 py-3 bg-gray-50 dark:bg-[#0A0A0A] border border-gray-200 dark:border-[#1A1A1A] rounded-xl focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-[#666666] transition-all duration-200"
              />
            </div>

            {/* Survey Options */}
            <motion.div
              whileHover={{ scale: 1.01 }}
              onClick={() => setSurveySettings(prev => ({ ...prev, anonymous: !prev.anonymous }))}
              className="flex items-center justify-between p-4 bg-gray-50 dark:bg-[#0A0A0A] rounded-xl cursor-pointer hover:bg-gray-100 dark:hover:bg-[#111111] transition-colors duration-200"
            >
              <div className="flex items-center gap-3">
                <div className={`w-4 h-4 rounded border-2 transition-all duration-200 flex items-center justify-center ${
                  surveySettings.anonymous 
                    ? 'bg-brand border-brand' 
                    : 'border-gray-300 dark:border-[#333333]'
                }`}>
                  {surveySettings.anonymous && (
                    <svg className="w-2.5 h-2.5 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">Anonymous Responses</span>
                  <p className="text-xs text-gray-600 dark:text-[#666666]">No client details collected</p>
                </div>
              </div>
            </motion.div>



            {/* Questions Builder */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-gray-900 dark:text-white">Survey Questions</label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setSurveySettings(prev => ({
                        ...prev,
                        questions: getDefaultSurveyQuestions()
                      }));
                    }}
                    className="px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-[#666666] hover:text-gray-900 dark:hover:text-white bg-gray-100 dark:bg-[#1A1A1A] hover:bg-gray-200 dark:hover:bg-[#2A2A2A] rounded-lg transition-colors"
                  >
                    Load Default Questions
                  </button>
                  <button
                    type="button"
                    onClick={addCustomQuestion}
                    className="px-3 py-1.5 text-xs font-medium text-brand hover:text-brand-hover bg-brand/10 hover:bg-brand/20 border border-brand/30 rounded-lg transition-colors flex items-center gap-1"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Add Question
                  </button>
                </div>
              </div>

              {/* Questions List */}
              <div className="space-y-3 max-h-96 overflow-y-auto">
                <AnimatePresence>
                  {surveySettings.questions.map((question, index) => (
                    <motion.div
                      key={question.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.2 }}
                      className="p-4 bg-gray-50 dark:bg-[#0A0A0A] border border-gray-200 dark:border-[#1A1A1A] rounded-xl"
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-6 h-6 bg-brand text-white rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 mt-1">
                          {index + 1}
                        </div>
                        <div className="flex-1 space-y-3">
                          {/* Question Text */}
                          <input
                            type="text"
                            value={question.question}
                            onChange={(e) => updateQuestion(question.id, { question: e.target.value })}
                            placeholder="Enter your question..."
                            className="w-full px-3 py-2 bg-white dark:bg-[#111111] border border-gray-200 dark:border-[#2A2A2A] rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-[#666666] focus:ring-2 focus:ring-brand/20 focus:border-brand"
                          />

                          {/* Question Type & Settings */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <select
                              value={question.type}
                              onChange={(e) => updateQuestion(question.id, { type: e.target.value })}
                              className="px-3 py-2 bg-white dark:bg-[#111111] border border-gray-200 dark:border-[#2A2A2A] rounded-lg text-sm text-gray-900 dark:text-white"
                            >
                              <option value="text">Text Response</option>
                              <option value="rating">Rating Scale</option>
                              <option value="yes_no">Yes/No</option>
                              <option value="multiple_choice">Multiple Choice</option>
                            </select>

                            <div className="flex items-center gap-2">
                              <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-[#666666]">
                                <input
                                  type="checkbox"
                                  checked={question.required}
                                  onChange={(e) => updateQuestion(question.id, { required: e.target.checked })}
                                  className="w-3 h-3 text-brand bg-gray-100 border-gray-300 rounded focus:ring-brand dark:focus:ring-brand dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                                />
                                Required
                              </label>
                            </div>
                          </div>

                          {/* Question Type Specific Options */}
                          {question.type === 'rating' && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              <select
                                value={question.options?.scale || 5}
                                onChange={(e) => updateQuestion(question.id, { 
                                  options: { ...question.options, scale: parseInt(e.target.value) }
                                })}
                                className="px-3 py-2 bg-white dark:bg-[#111111] border border-gray-200 dark:border-[#2A2A2A] rounded-lg text-sm text-gray-900 dark:text-white"
                              >
                                <option value={5}>1-5 Scale</option>
                                <option value={10}>1-10 Scale</option>
                              </select>
                              <input
                                type="text"
                                value={question.options?.labels?.[0] || ''}
                                onChange={(e) => updateQuestion(question.id, { 
                                  options: { 
                                    ...question.options, 
                                    labels: [e.target.value, question.options?.labels?.[1] || 'Excellent'] 
                                  }
                                })}
                                placeholder="Low label (e.g., Poor)"
                                className="px-3 py-2 bg-white dark:bg-[#111111] border border-gray-200 dark:border-[#2A2A2A] rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-[#666666]"
                              />
                            </div>
                          )}

                          {question.type === 'multiple_choice' && (
                            <div className="space-y-2">
                              <label className="text-xs text-gray-600 dark:text-[#666666]">Choice Options (one per line):</label>
                              <textarea
                                value={question.options?.choices?.join('\n') || ''}
                                onChange={(e) => updateQuestion(question.id, { 
                                  options: { 
                                    ...question.options, 
                                    choices: e.target.value.split('\n').filter(choice => choice.trim())
                                  }
                                })}
                                placeholder="Option 1&#10;Option 2&#10;Option 3"
                                rows={3}
                                className="w-full px-3 py-2 bg-white dark:bg-[#111111] border border-gray-200 dark:border-[#2A2A2A] rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-[#666666] resize-none"
                              />
                            </div>
                          )}

                          {question.type === 'text' && (
                            <input
                              type="text"
                              value={question.placeholder || ''}
                              onChange={(e) => updateQuestion(question.id, { placeholder: e.target.value })}
                              placeholder="Placeholder text for clients..."
                              className="w-full px-3 py-2 bg-white dark:bg-[#111111] border border-gray-200 dark:border-[#2A2A2A] rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-[#666666]"
                            />
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => removeQuestion(question.id)}
                          className="p-1 hover:bg-red-100 dark:hover:bg-red-900/20 text-red-500 rounded transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>

                {surveySettings.questions.length === 0 && (
                  <div className="text-center py-8 text-gray-500 dark:text-[#666666]">
                    <svg className="w-12 h-12 mx-auto mb-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p className="text-sm">No questions added yet</p>
                    <p className="text-xs mt-1">Use "Load Default Questions" or "Add Question" to get started</p>
                  </div>
                )}
              </div>
            </div>

            {/* Survey Preview */}
            {surveySettings.questions.length > 0 && (
                      <div className="p-4 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-[#0A0A0A] dark:to-[#111111] rounded-xl border border-gray-200 dark:border-[#1A1A1A]">
                        <div className="flex items-center gap-2 mb-3">
                          <svg className="w-4 h-4 text-brand" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                  <span className="text-xs font-medium text-gray-900 dark:text-white">Survey Preview</span>
                        </div>
                <div className="bg-white dark:bg-[#1A1A1A] rounded-lg p-4 border border-gray-200 dark:border-[#333333] space-y-3">
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    {surveySettings.surveyTitle || 'Feedback Survey'}
                          </div>
                  <div className="space-y-2">
                    {surveySettings.questions.slice(0, 3).map((q, index) => (
                      <div key={q.id} className="text-xs text-gray-600 dark:text-[#666666]">
                        {index + 1}. {q.question || 'Question text...'}
                        {q.type === 'rating' && <span className="text-brand ml-1">(1-{q.options?.scale || 5} scale)</span>}
                        {q.type === 'yes_no' && <span className="text-brand ml-1">(Yes/No)</span>}
                        {q.type === 'multiple_choice' && <span className="text-brand ml-1">(Multiple choice)</span>}
                        {q.required && <span className="text-red-500 ml-1">*</span>}
                          </div>
                    ))}
                    {surveySettings.questions.length > 3 && (
                      <div className="text-xs text-gray-500 dark:text-[#666666] italic">
                        + {surveySettings.questions.length - 3} more questions
                        </div>
                    )}
                      </div>
                </div>
              </div>
            )}
                    </motion.div>
                  )}
                </AnimatePresence>
    </div>
  );

  // Format date for consistent display
  const formatDate = (date) => {
    if (!date) return 'Not specified';
    if (typeof date === 'string') {
      const d = new Date(date);
      return !isNaN(d) ? d.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      }) : 'Invalid date';
    }
    if (date instanceof Date && !isNaN(date)) {
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }
    return 'Invalid date';
  };

  // Handle PDF download
  const handleDownloadPDF = () => {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 40;
    let yPos = margin;

    // Handle both old and new invoice structures
    const lineItems = invoiceData.lineItems && invoiceData.lineItems.length > 0 
      ? invoiceData.lineItems 
      : [{
          id: '1',
          description: invoiceData.project || 'Service',
          quantity: parseFloat(invoiceData.quantity) || 1,
          rate: parseFloat(invoiceData.rate) || parseFloat(invoiceData.amount) || 0,
          amount: (parseFloat(invoiceData.quantity) || 1) * (parseFloat(invoiceData.rate) || parseFloat(invoiceData.amount) || 0)
        }];

    // Header
    doc.setFontSize(28);
    doc.setFont('helvetica', 'bold');
    doc.text('INVOICE', margin, yPos);
    yPos += 40;

    // Invoice info
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.text(`Date: ${new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long', 
      day: 'numeric'
    })}`, margin, yPos);
    yPos += 25;

    // Bill To section
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Bill To:', margin, yPos);
    yPos += 20;
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(invoiceData.client || 'No client name', margin, yPos);
    if (invoiceData.email) {
      yPos += 18;
      doc.text(invoiceData.email, margin, yPos);
    }
    yPos += 30;

    // Enhanced table with sub-items support
    const tableData = [];
    lineItems.forEach(item => {
      // Add main line item
      tableData.push([
        item.description || 'Service',
        (item.quantity || 1).toString(),
        `${invoiceData.currency || 'USD'} ${(parseFloat(item.rate) || 0).toFixed(2)}`,
        `${invoiceData.currency || 'USD'} ${(parseFloat(item.amount) || 0).toFixed(2)}`
      ]);
      
      // Add sub-items if they exist
      if (item.subItems && item.subItems.length > 0) {
        item.subItems.forEach(subItem => {
          tableData.push([
            `  • ${subItem.text}`, // Indented with bullet point
            '—', // Empty quantity
            '—', // Empty rate
            '—'  // Empty amount
          ]);
        });
      }
    });

    doc.autoTable({
      startY: yPos,
      head: [['Description', 'Qty', 'Rate', 'Amount']],
      body: tableData,
      margin: { left: margin, right: margin },
      theme: 'grid',
      styles: { fontSize: 10, cellPadding: 8 },
      headStyles: {
        fillColor: [255, 178, 125],
        textColor: [0, 0, 0],
        fontStyle: 'bold'
      },
      columnStyles: {
        1: { halign: 'center' },
        2: { halign: 'right' },
        3: { halign: 'right' }
      }
    });
    
    yPos = doc.autoTable.previous.finalY + 25;

    // Summary section
    doc.setFontSize(11);
    const summaryX = pageWidth - 200;
    const valueX = pageWidth - margin;

    // Subtotal
    doc.setFont('helvetica', 'normal');
    doc.text('Subtotal:', summaryX, yPos);
    doc.text(`${invoiceData.currency || 'USD'} ${subtotal.toFixed(2)}`, valueX, yPos, { align: 'right' });
    yPos += 18;

    // Tax (if applicable)
    if (taxAmount > 0) {
      const taxLabel = invoiceData.taxType === 'percentage' 
        ? `Tax (${invoiceData.taxRate || 0}%):`
        : 'Tax:';
      doc.text(taxLabel, summaryX, yPos);
      doc.text(`${invoiceData.currency || 'USD'} ${taxAmount.toFixed(2)}`, valueX, yPos, { align: 'right' });
      yPos += 18;
    }

    // Total
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.text('Total:', summaryX, yPos);
    doc.text(`${invoiceData.currency || 'USD'} ${total.toFixed(2)}`, valueX, yPos, { align: 'right' });
    yPos += 30;

    // Due date
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.text(`Due Date: ${formatDate(invoiceData.dueDate)}`, margin, yPos);
    yPos += 30;



    // Footer
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text('Thank you for your business!', margin, yPos);
    yPos += 20;
    doc.text('Generated with Klorah • klorah.app', margin, yPos);

    // Save with better filename
    const dateStr = new Date().toISOString().split('T')[0];
    const clientName = (invoiceData.client || 'client').replace(/[^a-zA-Z0-9]/g, '-').toLowerCase();
    const filename = `invoice-${clientName}-${dateStr}.pdf`;
    doc.save(filename);
  };

  const renderReviewTab = () => {
    // Handle both old and new invoice structures
    const lineItems = invoiceData.lineItems && invoiceData.lineItems.length > 0 
      ? invoiceData.lineItems 
      : [{
          id: '1',
          description: invoiceData.project || 'Service',
          quantity: parseFloat(invoiceData.quantity) || 1,
          rate: parseFloat(invoiceData.rate) || parseFloat(invoiceData.amount) || 0,
          amount: (parseFloat(invoiceData.quantity) || 1) * (parseFloat(invoiceData.rate) || parseFloat(invoiceData.amount) || 0)
        }];

    return (
      <div className="space-y-6">
        {/* Header with Download Button */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Invoice Preview</h3>
            <p className="text-sm text-gray-500 dark:text-[#666666] mt-1">Review your invoice before sending to client</p>
          </div>
          <motion.button
            onClick={handleDownloadPDF}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="flex items-center gap-2 px-4 py-2.5 bg-brand hover:bg-brand-hover text-white font-medium rounded-xl transition-all duration-200 shadow-lg shadow-brand/20"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Download PDF
          </motion.button>
        </div>

        {/* Invoice Preview Card */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-black border border-gray-200 dark:border-[#1A1A1A] rounded-2xl shadow-lg shadow-brand/10"
        >
          {/* Invoice Content */}
          <div className="p-8 space-y-6">
            {/* Invoice Header */}
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white tracking-tight">INVOICE</h1>
                <div className="mt-4 space-y-1">
                  <p className="text-sm text-gray-600 dark:text-[#666666]">
                    <span className="font-medium">Date:</span> {new Date().toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })}
                  </p>
                </div>
              </div>
              
              {/* Status Badge */}
              <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/50 rounded-xl">
                <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></div>
                <span className="text-sm font-medium text-amber-700 dark:text-amber-300">Draft</span>
              </div>
            </div>

            {/* Bill To Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide mb-2">Bill To</h3>
                <div className="space-y-1">
                  <p className="font-medium text-gray-900 dark:text-white">{invoiceData.client || 'No client name'}</p>
                  {invoiceData.email && (
                    <p className="text-sm text-gray-600 dark:text-[#666666]">{invoiceData.email}</p>
                  )}
                </div>
              </div>
              
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide mb-2">Project Details</h3>
                <div className="space-y-1">
                  <p className="font-medium text-gray-900 dark:text-white">{invoiceData.project || 'Service'}</p>
                  <p className="text-sm text-gray-600 dark:text-[#666666]">
                    Due: {formatDate(invoiceData.dueDate)}
                  </p>
                </div>
              </div>
            </div>

            {/* Line Items Table */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide">Items & Services</h3>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2 border-gray-200 dark:border-[#1A1A1A]">
                      <th className="text-left py-3 text-sm font-semibold text-gray-900 dark:text-white">Description</th>
                      <th className="text-center py-3 text-sm font-semibold text-gray-900 dark:text-white w-20">Qty</th>
                      <th className="text-right py-3 text-sm font-semibold text-gray-900 dark:text-white w-24">Rate</th>
                      <th className="text-right py-3 text-sm font-semibold text-gray-900 dark:text-white w-28">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-[#1A1A1A]">
                    {lineItems.map((item, index) => (
                      <Fragment key={item.id || index}>
                        <motion.tr 
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className="group hover:bg-gray-50 dark:hover:bg-[#0A0A0A] transition-colors"
                        >
                          <td className="py-3 text-sm text-gray-900 dark:text-white font-medium">
                            {item.description || 'Service'}
                          </td>
                          <td className="py-3 text-center text-sm text-gray-600 dark:text-[#666666]">
                            {item.quantity || 1}
                          </td>
                          <td className="py-3 text-right text-gray-600 dark:text-[#666666] font-mono text-sm">
                            {invoiceData.currency || 'USD'} {(parseFloat(item.rate) || 0).toFixed(2)}
                          </td>
                          <td className="py-3 text-right text-sm text-gray-900 dark:text-white font-semibold font-mono">
                            {invoiceData.currency || 'USD'} {(parseFloat(item.amount) || 0).toFixed(2)}
                          </td>
                        </motion.tr>
                        {/* Sub-items for this line item */}
                        {item.subItems && item.subItems.length > 0 && item.subItems.map((subItem, subIndex) => (
                          <motion.tr
                            key={`${item.id}-sub-${subIndex}`}
                            initial={{ opacity: 0, x: -5 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: (index * 0.1) + (subIndex * 0.05) + 0.1 }}
                            className="bg-gray-50/50 dark:bg-[#0A0A0A]/50"
                          >
                            <td className="py-1.5 pl-6 text-xs text-gray-600 dark:text-[#666666]">
                              <div className="flex items-center gap-2">
                                <div className="w-1 h-1 bg-brand rounded-full flex-shrink-0"></div>
                                <span>{subItem.text}</span>
                              </div>
                            </td>
                            <td className="py-1.5 text-center text-xs text-gray-400 dark:text-[#555555]">
                              —
                            </td>
                            <td className="py-1.5 text-right text-xs text-gray-400 dark:text-[#555555]">
                              —
                            </td>
                            <td className="py-1.5 text-right text-xs text-gray-400 dark:text-[#555555]">
                              —
                            </td>
                          </motion.tr>
                        ))}
                       </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Summary Section */}
            <div className="flex justify-end">
              <div className="w-full max-w-sm space-y-2">
                <div className="flex justify-between items-center py-1.5">
                  <span className="text-sm text-gray-600 dark:text-[#666666] font-medium">Subtotal</span>
                                      <span className="font-semibold text-sm text-gray-900 dark:text-white font-mono">
                      {getCurrencySymbol(invoiceData.currency || 'USD')}{subtotal.toFixed(2)}
                    </span>
                </div>
                
                {taxAmount > 0 && (
                  <motion.div 
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="flex justify-between items-center py-1.5"
                  >
                    <span className="text-sm text-gray-600 dark:text-[#666666] font-medium">
                      {invoiceData.taxType === 'percentage' 
                        ? `Tax (${invoiceData.taxRate || 0}%)`
                        : 'Tax'
                      }
                    </span>
                    <span className="font-semibold text-sm text-gray-900 dark:text-white font-mono">
                      {getCurrencySymbol(invoiceData.currency || 'USD')}{taxAmount.toFixed(2)}
                    </span>
              </motion.div>
            )}
                
                <div className="border-t-2 border-gray-200 dark:border-[#1A1A1A] pt-2">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-bold text-gray-900 dark:text-white">Total</span>
                    <span className="text-xl font-bold text-gray-900 dark:text-white font-mono">
                      {getCurrencySymbol(invoiceData.currency || 'USD')}{total.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </div>



            {/* Footer */}
            <div className="border-t border-gray-100 dark:border-[#1A1A1A] pt-4 text-center">
              <p className="text-sm text-gray-600 dark:text-[#666666] mb-2">Thank you for your business!</p>
              <p className="text-xs text-gray-500 dark:text-[#999999]">
                Generated with <span className="text-brand font-medium">Klorah</span> • klorah.app
              </p>
            </div>
          </div>
        </motion.div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white dark:bg-[#0A0A0A] border border-gray-200 dark:border-[#1A1A1A] rounded-xl p-4"
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-brand/20 rounded-lg flex items-center justify-center">
                <svg className="w-4 h-4 text-brand" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-[#666666]">Due Date</p>
                <p className="text-sm font-medium text-gray-900 dark:text-white">{formatDate(invoiceData.dueDate)}</p>
              </div>
            </div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white dark:bg-[#0A0A0A] border border-gray-200 dark:border-[#1A1A1A] rounded-xl p-4"
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-brand/20 rounded-lg flex items-center justify-center">
                <svg className="w-4 h-4 text-brand" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                </svg>
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-[#666666]">Line Items</p>
                <p className="text-sm font-medium text-gray-900 dark:text-white">{lineItems.length} item{lineItems.length !== 1 ? 's' : ''}</p>
              </div>
            </div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white dark:bg-[#0A0A0A] border border-gray-200 dark:border-[#1A1A1A] rounded-xl p-4"
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-brand/20 rounded-lg flex items-center justify-center">
                <svg className="w-4 h-4 text-brand" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-[#666666]">Email Delivery</p>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {getEmailMode() === 'none' ? 'Manual' : 'Automatic'}
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Additional Settings Summary */}
        <div className="bg-gray-50 dark:bg-[#0A0A0A] border border-gray-200 dark:border-[#1A1A1A] rounded-xl p-6">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Configuration Summary</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-600 dark:text-[#666666]">Email Style:</span>
              <span className="ml-2 text-gray-900 dark:text-white capitalize">{invoiceData.emailStyle}</span>
            </div>
            <div>
              <span className="text-gray-600 dark:text-[#666666]">Payment Email:</span>
              <span className="ml-2 text-gray-900 dark:text-white">
                {paymentEmailSettings.sendConfirmationEmail ? 'Enabled' : 'Disabled'}
              </span>
            </div>
            <div>
              <span className="text-gray-600 dark:text-[#666666]">Client Survey:</span>
              <span className="ml-2 text-gray-900 dark:text-white">
                {surveySettings.createSurvey ? `${surveySettings.questions.length} questions` : 'Disabled'}
              </span>
            </div>
            <div>
              <span className="text-gray-600 dark:text-[#666666]">Currency:</span>
              <span className="ml-2 text-gray-900 dark:text-white">{invoiceData.currency}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Handle submit with full functionality
  const handleSubmit = async () => {
    // Reset error states
    setError('');
    setStripeCapabilityError(false);
    setEmailAccountError(false);
    
    // Basic validations
    if (!isOnboarded || !stripeAccountId) {
      setError('Please set up your Stripe account first.');
      return;
    }
    if (!user) {
      setError('You must be logged in to create an invoice.');
      return;
    }
    if (!invoiceData.client || !invoiceData.project) {
      setError('Please fill out required fields (client, project).');
      return;
    }

    // Calculate final total to validate
    const finalTotal = total;
    
    // Prevent creating invoices with negative totals (customer should never owe negative amounts)
    if (finalTotal < 0) {
      setError('Invoice total cannot be negative. Please adjust your line items or discounts so the customer owes a positive amount.');
      setIsSubmitting(false);
      return;
    }
    
    // Relaxed validation - allow invoices with or without amounts/line items
    // This allows for flexible invoice creation as requested by the user
    const hasLineItems = invoiceData.lineItems.length > 0;
    const hasSimpleAmount = invoiceData.amount && !hasLineItems;
    
    // No other validation required - users can create invoices with any combination they prefer

    setIsSubmitting(true);
    try {
      // Create the invoice in Firestore
      const invoiceService = new InvoiceService(user);
      
      // Ensure business info is always saved to invoice
      const businessInfoToSave = invoiceData.businessInfo && Object.keys(invoiceData.businessInfo).length > 0 
        ? invoiceData.businessInfo 
        : {
            businessName: invoiceData.fromName || '',
            professionalTitle: '',
            email: user.email || '',
            phone: '',
            location: ''
          };

      // Prepare invoice data for submission
      let invoiceToSubmit = {
        ...invoiceData,
        currency: invoiceData.currency || 'USD',
        connectedAccountId: stripeAccountId,
        createdAt: new Date().toISOString(),
        userId: user.uid,
        paymentEmailSettings: paymentEmailSettings,  // Add payment email settings
        surveySettings: surveySettings,  // Add survey settings
        // Include customization fields
        fromName: invoiceData.fromName || businessInfoToSave.businessName || '',
        clientName: invoiceData.clientName || invoiceData.client || '',
        businessInfo: businessInfoToSave  // Always save business info
      };

      // Handle line items with maximum flexibility
      if (invoiceData.lineItems.length > 0) {
        // Keep all line items, even if they have no description or rate
        // This allows users to create invoices with any combination they prefer
        invoiceToSubmit.lineItems = invoiceData.lineItems;
        invoiceToSubmit.amount = total;
      } else {
        // Simple mode - create a single line item from quantity and rate
        const quantity = parseFloat(invoiceData.quantity) || 1;
        const rate = parseFloat(invoiceData.rate) || parseFloat(invoiceData.amount) || 0;
        const amount = quantity * rate;
        
        invoiceToSubmit.lineItems = [{
          id: '1',
          description: invoiceData.project || 'Service',
          quantity: quantity,
          rate: rate,
          amount: amount
        }];
        invoiceToSubmit.amount = total;
        invoiceToSubmit.subtotal = amount;
      }

      const invoiceId = await invoiceService.createInvoice(invoiceToSubmit);

      // Create survey immediately if enabled (while user is authenticated)
      if (surveySettings.createSurvey) {
        try {
          console.log('🔍 Creating survey for invoice:', invoiceId);
          const firestore = getFirestore();
          const surveyDoc = {
            title: surveySettings.surveyTitle || `Feedback for ${invoiceData.project || 'Project'}`,
            description: `We'd love to get your feedback on the ${invoiceData.project || 'project'} we completed for you.`,
            questions: surveySettings.questions || [
              {
                id: '1',
                type: 'rating',
                question: 'How satisfied are you with the work completed?',
                required: true,
                options: { scale: 5, labels: ['Very Unsatisfied', 'Unsatisfied', 'Neutral', 'Satisfied', 'Very Satisfied'] }
              },
              {
                id: '2',
                type: 'text',
                question: 'What did you like most about working with us?',
                required: false
              },
              {
                id: '3',
                type: 'text',
                question: 'How can we improve our service?',
                required: false
              }
            ],
            userId: user.uid,
            invoiceId: invoiceId,
            clientName: invoiceData.client || invoiceData.clientName,
            clientEmail: invoiceData.email,
            projectName: invoiceData.project || invoiceData.projectName,
            status: 'active', // Set to active immediately so unauthenticated users can respond after payment
            anonymous: surveySettings.anonymous || false,
            createdAt: new Date(),
            updatedAt: new Date(),
            responses: [],
            responseCount: 0,
            autoCreated: true // Flag to indicate this was auto-created from invoice settings
          };

          const docRef = await addDoc(collection(firestore, 'surveys'), surveyDoc);
          console.log('✅ Survey created with ID:', docRef.id);
        } catch (surveyError) {
          console.error('❌ Error creating survey:', surveyError);
          // Don't fail the invoice creation if survey creation fails
        }
      }

      // Track invoice creation in analytics
      await trackInvoiceCreated({
        id: invoiceId,
        amount: total,
        currency: invoiceData.currency || 'USD',
        clientId: invoiceData.clientId,
        dueDate: invoiceData.dueDate.toISOString(),
        hasLineItems: invoiceData.lineItems.length > 0,
        lineItemCount: invoiceData.lineItems.length,
        taxRate: invoiceData.taxRate,
        projectId: project?.id,
        hasSurvey: surveySettings.createSurvey,
        surveyQuestionCount: surveySettings.questions.length
      });

      // Update project with invoice reference if this is a project-specific invoice
      if (project?.id) {
        const db = getFirestore();
        const projectRef = doc(db, 'projects', project.id);
        await updateDoc(projectRef, {
          invoices: arrayUnion(invoiceId)
        });
      }

      const emailMode = getEmailMode();
      const invoiceUrl = `https://klorah.app/preview-invoice/${invoiceId}`;

      // Get valid recipients (from recipients array or fallback to single email)
      const validRecipients = invoiceData.recipients.filter(r => r.email.trim());
      const recipientsToSend = validRecipients.length > 0 ? validRecipients : 
        (invoiceData.email ? [{ email: invoiceData.email }] : []);

      if (emailMode === 'auto' && recipientsToSend.length > 0) {
        // Check if user has email accounts connected
        if (userAccounts.length === 0) {
          setError('To send emails, please connect an email account first.');
          setEmailAccountError(true);
          return;
        }
        
        // Convert plain text to HTML if needed
        const finalEmailBody = invoiceData.customEmailMessage 
          ? invoiceData.customEmailMessage.split('\n').map(line => 
              line.includes('[View Invoice Button]') 
                ? `<p><a href="${invoiceUrl}" style="display: inline-block; background-color: #f97316; color: white; padding: 10px 20px; text-decoration: none; border-radius: 5px; margin: 10px 0;">View Invoice</a></p>`
                : `<p>${line}</p>`
            ).join('')
          : `<p>Your invoice for ${invoiceData.project} is ready for payment.</p><p><a href="${invoiceUrl}" style="display: inline-block; background-color: #f97316; color: white; padding: 10px 20px; text-decoration: none; border-radius: 5px; margin: 10px 0;">View Invoice</a></p>`;
          
        // Add professional signature
        const signature = `
          <div style="margin-top: 40px; padding-top: 20px; border-top: 1px solid #eee;">
            <p style="color: #666; font-size: 12px; margin: 0;">
              Sent with <a href="https://klorah.app" style="color: #f97316; text-decoration: none; font-weight: 500;">Klorah</a>
            </p>
          </div>`;
          
        const finalEmailBodyWithSignature = finalEmailBody + signature;
        
        // Send emails to all recipients
        try {
          const accountId = userAccounts.length > 0 ? userAccounts[0].accountId : '';
          let emailsSentCount = 0;
          
          // Send to each recipient
          for (const recipient of recipientsToSend) {
            try {
              await EmailService.sendEmail({
                user_id: user.uid,
                to: recipient.email,
                subject: `Invoice for ${invoiceData.project}`,
                body: finalEmailBodyWithSignature,
                account_id: accountId
              });
              emailsSentCount++;
            } catch (recipientError) {
              console.error(`Failed to send email to ${recipient.email}:`, recipientError);
            }
          }
          
          // Update invoice with sentDate when first email is successfully sent
          if (emailsSentCount > 0) {
            const db = getFirestore();
            const invoiceRef = doc(db, 'invoices', invoiceId);
            await updateDoc(invoiceRef, {
              sentDate: new Date(),
              updatedAt: new Date()
            });
          }
          
          // Track invoice email sent
          await trackInvoiceSent({
            id: invoiceId,
            amount: total,
            clientId: invoiceData.clientId,
            deliveryMethod: 'email'
          });
          
          if (emailsSentCount === recipientsToSend.length) {
            toast.success(`Invoice created and sent to ${emailsSentCount} recipient${emailsSentCount > 1 ? 's' : ''}!`);
          } else if (emailsSentCount > 0) {
            toast.success(`Invoice created! Sent to ${emailsSentCount} of ${recipientsToSend.length} recipients.`);
          } else {
            toast.success('Invoice created! (All emails failed to send)');
          }
          
          onClose();
          navigate(`/preview-invoice/${invoiceId}`);
        } catch (error) {
          console.error('Failed to send emails:', error);
          toast.success('Invoice created! (Email failed to send)');
          onClose();
          navigate(`/preview-invoice/${invoiceId}`);
        }
      } else {
        // No email sending - just create and redirect to preview
        toast.success('Invoice created!');
        onClose();
        navigate(`/preview-invoice/${invoiceId}`);
      }
    } catch (err) {
      console.error('Error creating invoice:', err);
      
      // Check if the error is related to Stripe capabilities
      const errorStr = err.toString().toLowerCase();
      if (
        errorStr.includes('capabilities') || 
        (errorStr.includes('paymentintent') && 
         (errorStr.includes('transfers') || 
          errorStr.includes('crypto_transfers') || 
          errorStr.includes('legacy_payments')))
      ) {
        setStripeCapabilityError(true);
        setError('Your Stripe account needs additional capabilities enabled to receive payments. Please click the button below to update your Stripe account settings.');
      } else {
        setError('Failed to create invoice. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 dark:bg-black/80 backdrop-blur-md flex items-center justify-center p-2 sm:p-4 z-[999]">
      <div className="bg-white dark:bg-black rounded-2xl sm:rounded-3xl shadow-2xl w-full max-w-6xl max-h-[98vh] sm:max-h-[95vh] overflow-hidden flex flex-col sm:flex-row border border-gray-200 dark:border-[#1A1A1A]">
        
        {/* Mobile Top Tabs - Horizontal scroll */}
        <div className="sm:hidden w-full bg-gray-50 dark:bg-[#0A0A0A] border-b border-gray-200 dark:border-[#1A1A1A]">
          {/* Mobile Header */}
          <div className="px-4 py-4 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Create Invoice</h2>
              <p className="text-xs text-gray-500 dark:text-[#666666] mt-0.5">Set up your professional invoice</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-[#1A1A1A] rounded-full transition-colors text-gray-500 dark:text-[#666666] hover:text-gray-700 dark:hover:text-white">
              {/* <svg className="w-5 h-5" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
              </svg> */}
            </button>
          </div>

          {/* Mobile Tab Navigation - Horizontal scroll */}
          <div className="overflow-x-auto scrollbar-hide">
            <div className="flex gap-2 p-3 min-w-max">
              {tabs.map((tab, index) => (
                <motion.button
                  key={tab.id}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-shrink-0 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                    activeTab === tab.id
                      ? 'bg-brand text-white shadow-lg shadow-brand/20'
                      : 'text-gray-600 dark:text-[#666666] bg-white dark:bg-[#111111] hover:text-gray-900 dark:hover:text-white border border-gray-200 dark:border-[#2A2A2A]'
                  }`}
                >
                  <span className="whitespace-nowrap">
                    {index + 1}. {tab.name}
                    {tab.beta && (
                      <span className="ml-1.5 px-1.5 py-0.5 bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 text-xs font-medium rounded-md">
                        BETA
                      </span>
                    )}
                  </span>
                </motion.button>
              ))}
            </div>
          </div>

          {/* Mobile Fee Info */}
          <div className="px-3 pb-3">
            <div className="p-2.5 bg-gray-100 dark:bg-gray-800/50 rounded-lg">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Fee: 2.9% paid by {invoiceData.feePayer === 'creator' ? 'you' : 'customer'}
              </p>
            </div>
          </div>
        </div>

        {/* Desktop Left Sidebar - Tabs */}
        <div className="hidden sm:flex w-80 bg-gray-50 dark:bg-[#0A0A0A] border-r border-gray-200 dark:border-[#1A1A1A] flex-col">
          {/* Header */}
          <div className="px-6 py-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Create Invoice</h2>
            <p className="text-sm text-gray-500 dark:text-[#666666] mt-1">Set up your professional invoice</p>
          </div>

          {/* Navigation Tabs */}
          <div className="flex-1 p-4 space-y-2">
            {tabs.map((tab) => (
              <motion.button
                key={tab.id}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center px-4 py-3 rounded-xl text-left transition-all duration-200 ${
                  activeTab === tab.id
                    ? 'bg-brand text-white shadow-lg shadow-brand/20'
                    : 'text-gray-600 dark:text-[#666666] hover:bg-white dark:hover:bg-[#111111] hover:text-gray-900 dark:hover:text-white hover:shadow-sm hover:shadow-brand/10'
                }`}
              >
                <div className="flex items-center">
                  <span className="font-medium">{tab.name}</span>
                  {tab.beta && (
                    <span className="ml-2 px-2 py-0.5 bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 text-xs font-medium rounded-md">
                      BETA
                    </span>
                  )}
                </div>
              </motion.button>
            ))}
          </div>

          {/* Footer Info */}
          <div className="p-4 border-t border-gray-200 dark:border-[#1A1A1A]">
            <div className="p-3 bg-gray-100 dark:bg-gray-800/50 rounded-xl">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Fee: 2.9% paid by {invoiceData.feePayer === 'creator' ? 'you' : 'customer'}
              </p>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col min-h-0">
          {/* Desktop Header */}
          <div className="hidden sm:flex px-8 py-6 bg-white dark:bg-black items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                {tabs.find(tab => tab.id === activeTab)?.name}
              </h3>
              <p className="text-sm text-gray-500 dark:text-[#666666] mt-0.5">
                {activeTab === 'basics' && 'Enter client details and pricing information'}
                {activeTab === 'email' && 'Configure email delivery settings'}
                {activeTab === 'survey' && 'Set up client feedback collection'}
                {activeTab === 'review' && 'Review and finalize your invoice'}
              </p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-[#1A1A1A] rounded-full transition-colors text-gray-500 dark:text-[#666666] hover:text-gray-700 dark:hover:text-white">
              <svg className="w-5 h-5" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Mobile Header */}
          <div className="sm:hidden px-4 py-3 bg-white dark:bg-black">
            <h3 className="text-base font-medium text-gray-900 dark:text-white">
              {tabs.find(tab => tab.id === activeTab)?.name}
            </h3>
            <p className="text-xs text-gray-500 dark:text-[#666666] mt-0.5">
              {activeTab === 'basics' && 'Enter client details and pricing'}
              {activeTab === 'email' && 'Configure email delivery'}
              {activeTab === 'survey' && 'Set up client feedback'}
              {activeTab === 'review' && 'Review and finalize'}
            </p>
          </div>

          {/* Content Area */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-8">
            {error && (
              <div className="mb-4 sm:mb-6 text-sm bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 p-3 sm:p-4 rounded-xl sm:rounded-2xl">
                <p className="text-red-600 dark:text-red-300">{error}</p>
                {stripeCapabilityError && (
                  <button
                    onClick={handleOpenStripeDashboard}
                    className="mt-3 bg-brand hover:bg-brand-hover text-white px-4 py-2 rounded-xl text-sm font-medium transition-colors w-full sm:w-auto"
                  >
                    Update Stripe Account
                  </button>
                )}
                {emailAccountError && (
                  <button
                    onClick={() => setShowEmailAccountsModal(true)}
                    className="mt-3 bg-brand hover:bg-brand-hover text-white px-4 py-2 rounded-xl text-sm font-medium transition-colors w-full sm:w-auto"
                  >
                    Connect Email Account
                  </button>
                )}
              </div>
            )}

            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                {renderTabContent()}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Footer Actions */}
          <div className="px-4 sm:px-8 py-4 sm:py-6 border-t border-gray-200 dark:border-[#1A1A1A] bg-white dark:bg-black">
            {/* Mobile Footer - Stacked */}
            <div className="sm:hidden space-y-3">
              <div className="flex gap-2">
                {activeTab !== 'basics' && (
                  <button
                    onClick={() => {
                      const currentIndex = tabs.findIndex(tab => tab.id === activeTab);
                      if (currentIndex > 0) {
                        setActiveTab(tabs[currentIndex - 1].id);
                      }
                    }}
                    className="flex-1 px-4 py-3 text-sm font-medium bg-gray-100 dark:bg-[#1A1A1A] hover:bg-gray-200 dark:hover:bg-[#2A2A2A] rounded-xl transition-colors text-gray-700 dark:text-[#666666] hover:text-gray-900 dark:hover:text-white border border-gray-200 dark:border-[#2A2A2A]"
                  >
                    ← Previous
                  </button>
                )}
                <button
                  className="flex-1 px-4 py-3 text-sm font-medium bg-gray-100 dark:bg-[#1A1A1A] hover:bg-gray-200 dark:hover:bg-[#2A2A2A] rounded-xl transition-colors text-gray-700 dark:text-[#666666] hover:text-gray-900 dark:hover:text-white border border-gray-200 dark:border-[#2A2A2A]"
                  onClick={onClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
              </div>
              
              {activeTab === 'review' ? (
                <button
                  className={`w-full px-4 py-3 text-sm font-medium rounded-xl transition-colors ${
                    isSubmitting
                      ? 'bg-gray-200 dark:bg-[#2A2A2A] opacity-50 cursor-not-allowed text-gray-500 dark:text-[#666666]'
                      : 'bg-brand hover:bg-brand-hover text-white shadow-lg shadow-brand/20'
                  }`}
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Creating...' : 'Create Invoice'}
                </button>
              ) : (
                <button
                  onClick={() => {
                    const currentIndex = tabs.findIndex(tab => tab.id === activeTab);
                    if (currentIndex < tabs.length - 1) {
                      setActiveTab(tabs[currentIndex + 1].id);
                    }
                  }}
                  className="w-full px-4 py-3 text-sm font-medium bg-brand hover:bg-brand-hover text-white rounded-xl transition-colors shadow-lg shadow-brand/20"
                >
                  Next Step →
                </button>
              )}
            </div>

            {/* Desktop Footer - Horizontal */}
            <div className="hidden sm:flex justify-between">
              <div className="flex gap-3">
                {activeTab !== 'basics' && (
                  <button
                    onClick={() => {
                      const currentIndex = tabs.findIndex(tab => tab.id === activeTab);
                      if (currentIndex > 0) {
                        setActiveTab(tabs[currentIndex - 1].id);
                      }
                    }}
                    className="px-6 py-3 text-sm font-medium bg-gray-100 dark:bg-[#1A1A1A] hover:bg-gray-200 dark:hover:bg-[#2A2A2A] rounded-2xl transition-colors text-gray-700 dark:text-[#666666] hover:text-gray-900 dark:hover:text-white border border-gray-200 dark:border-[#2A2A2A]"
                  >
                    Previous
                  </button>
                )}
              </div>

              <div className="flex gap-3">
                <button
                  className="px-6 py-3 text-sm font-medium bg-gray-100 dark:bg-[#1A1A1A] hover:bg-gray-200 dark:hover:bg-[#2A2A2A] rounded-2xl transition-colors text-gray-700 dark:text-[#666666] hover:text-gray-900 dark:hover:text-white border border-gray-200 dark:border-[#2A2A2A]"
                  onClick={onClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                
                {activeTab === 'review' ? (
                  <button
                    className={`px-6 py-3 text-sm font-medium rounded-2xl transition-colors ${
                      isSubmitting
                        ? 'bg-gray-200 dark:bg-[#2A2A2A] opacity-50 cursor-not-allowed text-gray-500 dark:text-[#666666]'
                        : 'bg-brand hover:bg-brand-hover text-white'
                    }`}
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Creating...' : 'Create Invoice'}
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      const currentIndex = tabs.findIndex(tab => tab.id === activeTab);
                      if (currentIndex < tabs.length - 1) {
                        setActiveTab(tabs[currentIndex + 1].id);
                      }
                    }}
                    className="px-6 py-3 text-sm font-medium bg-brand hover:bg-brand-hover text-white rounded-2xl transition-colors"
                  >
                    Next
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Email Template Manager Modal */}
      <EmailTemplateManager
        isOpen={showTemplateManager}
        onClose={() => setShowTemplateManager(false)}
      />

      {/* Email Accounts Modal */}
      <EmailAccountsModal
        isOpen={showEmailAccountsModal}
        onClose={() => setShowEmailAccountsModal(false)}
      />
    </div>
  );
};

export default InvoiceModal;
